/*
 * Document Service - CRUD and Business Logic Functions
 *
 * This service contains CRUD operations and business logic for documents.
 * For data reading, use useLiveQuery from Dexie React hooks to access the database directly.
 */

import db from '@/db';
import { Document } from '@/db';
import type { SuperFormCRDTMetadata, ServerFieldErrors } from '../types/document';
import { finishTarget, isDocumentLocked, isUnderReview } from '../types/document';
import { isBlank } from '@mes-amis/super-form';
import type { FieldPermissions } from '@mes-amis/super-form';
import type { ExternalError, SuperFormDefinition } from '@mes-amis/super-form';
import type { DocumentTemplate } from '../types/document_template';
import { buildDuplicatePrefills } from './duplicate_prefill';
import type { AnalyticsEventData, SyncErrorType } from '../types/analytics_event';
import { documentPath } from '@/utils/routes';
import { API_ENDPOINTS, MONAMI_HOST } from '../../config/env';
import { getCurrentPerson } from './user_service';
import { AuthExpiredError, fetchWithAuth } from '../../lib/fetch-utils';
import { softSignOut } from '../../lib/auth';
import { fetchFromApi } from '../../lib/api-client';
import { fetchCollection } from '../../lib/paged-collection';
import { deleteLocalSignatures, fetchSignatures, getLocalSignatures, storeServerSignatures, submitSignature } from './signature_service';
import { getSignatureLabel, getSignatureSlotId, scopedLocalId, slotIdFromLocalId, type Signature } from '../types/signature';
import { extractContactIds, getUnsyncedContactsByIds, replaceContactIdsInSuperForm, syncContact } from './contact_service';
import { getClientUUID } from '../../lib/crdt/client-uuid';
import { HybridLogicalClockService } from '../../lib/crdt/hlc';
import { mergeDocumentCRDT, type MergeResult } from '../../lib/crdt/document-crdt';
import { createLogger, LogLevel } from '../../lib/logger';
import { findTelFields, convertTelFieldsToE164 } from '../../lib/phone-format';
import { isOnline } from '../../lib/connectivity';
import { dateCompletedValue } from '../../lib/recorded_at';
import { lockedOnlineCopy } from '../../lib/locked_online';
import { buildSeedPayload } from './icode_prefill_seed';
import { trackEvent, trackEventWithPerson } from './analytics_service';
import {
  acknowledgeWarningDismissals,
  finalizeWarnings,
  mergeWarningDismissals,
  pendingWarningDismissals,
  serverWarningDismissals,
  warningDismissalStamps,
  type WarningDismissalRecord,
  type WarningDismissalUpload,
} from '../../lib/warning_dismissals';

const logger = createLogger('DocumentService', LogLevel.INFO);

// Module-level HLC instance cache — reused across calls within page lifetime
// to ensure monotonically increasing timestamps even for rapid edits in the same ms.
let hlcInstance: HybridLogicalClockService | null = null;
let hlcClientUUID: string | null = null;

function getHLC(clientUUID: string): HybridLogicalClockService {
  if (!hlcInstance || hlcClientUUID !== clientUUID) {
    hlcInstance = new HybridLogicalClockService(clientUUID);
    hlcClientUUID = clientUUID;
  }
  return hlcInstance;
}

/**
 * Advance the local HLC by receiving remote timestamps from a CRDT merge.
 * Call this after merging remote document data to maintain causality tracking.
 */
export function advanceHLCFromRemoteTimestamps(
  clientUUID: string,
  mergedMeta: SuperFormCRDTMetadata | undefined
): void {
  if (!mergedMeta) return;
  const hlc = getHLC(clientUUID);
  for (const timestamp of Object.values(mergedMeta)) {
    try {
      const remoteClock = HybridLogicalClockService.fromString(timestamp);
      if (remoteClock.client_uuid !== clientUUID) {
        hlc.receive(remoteClock);
      }
    } catch {
      // Skip malformed timestamps
    }
  }
}

/**
 * The slugs in a field_permissions map the current person may NOT view
 * (mapped to `false`). Absent or `true` means viewable.
 */
function restrictedSlugs(fieldPermissions: FieldPermissions | undefined): Set<string> {
  if (!fieldPermissions) return new Set();
  const result = new Set<string>();
  for (const [slug, viewable] of Object.entries(fieldPermissions)) {
    if (viewable === false) result.add(slug);
  }
  return result;
}

/**
 * Derive a draft's `field_permissions` from a client's cached
 * `core_data_snapshot`. A fresh offline draft has no server-issued
 * field_permissions yet, so without this a restricted field would render
 * unmasked until the first sync. The server already strips slugs the syncing
 * person may not view from the snapshot (and keeps viewable-but-blank keys), so
 * a slug present in the snapshot is viewable; absent ⇒ restricted.
 *
 * `permissionedSlugs` is the template's server-emitted `permissioned_slugs`
 * (the canonical FIELD_PERMISSIONS keys present in its definition). The API
 * always emits the list (possibly empty), so callers pass it through.
 */
export function deriveFieldPermissionsFromSnapshot(
  snapshot: Record<string, unknown> | undefined,
  permissionedSlugs: readonly string[]
): FieldPermissions {
  const permissions: FieldPermissions = {};
  for (const slug of permissionedSlugs) {
    permissions[slug] = !!snapshot && slug in snapshot;
  }
  return permissions;
}

/**
 * Drop restricted slugs (and their CRDT metadata) from a super_form/meta pair.
 * The server strips restricted values from the read payload, so the client
 * should never hold one — but if a stale value lingers locally we must not let
 * it leave the device or survive a merge. Returns new objects; inputs untouched.
 */
function stripRestrictedFields(
  superForm: Record<string, unknown> | undefined,
  meta: SuperFormCRDTMetadata | undefined,
  restricted: Set<string>
): { superForm: Record<string, unknown> | undefined; meta: SuperFormCRDTMetadata | undefined } {
  if (restricted.size === 0) return { superForm, meta };

  let strippedForm = superForm;
  if (superForm) {
    strippedForm = { ...superForm };
    for (const slug of restricted) delete strippedForm[slug];
  }

  let strippedMeta = meta;
  if (meta) {
    strippedMeta = { ...meta };
    for (const slug of restricted) {
      // Drop both the field's own CRDT timestamp and its prefill-confirmation
      // marker (`confirmed_prefill:<slug>`) so a restricted slug name — not just
      // its value — never leaves the device.
      delete strippedMeta[slug];
      delete strippedMeta[`confirmed_prefill:${slug}`];
    }
  }

  return { superForm: strippedForm, meta: strippedMeta };
}

/**
 * Build the LOCAL side of a CRDT merge with the fields this device cannot author
 * server state for removed: template virtual/disabled fields and restricted
 * sensitive slugs. Left in, their locally-held values register as spurious local
 * wins on every reconciliation (the server payload never contains them), marking
 * the doc unsynced and scheduling redundant pushes; stripping restricted slugs
 * also guarantees a stale sensitive value never survives the merge or leaves the
 * device. Callers supply the virtual-field set because it is resolved differently
 * by transaction scope — getVirtualFields reads db.documentTemplates, which a
 * caller inside a write transaction must prefetch outside it.
 */
export function stripUnauthorableLocal(
  existingDoc: Pick<Document, 'super_form' | 'super_form_meta'>,
  virtualFields: Set<string>,
  fieldPermissions: FieldPermissions | undefined
): { superForm: Record<string, unknown> | undefined; meta: SuperFormCRDTMetadata | undefined } {
  const unauthorable = new Set<string>([...virtualFields, ...restrictedSlugs(fieldPermissions)]);
  return stripRestrictedFields(existingDoc.super_form, existingDoc.super_form_meta, unauthorable);
}

/**
 * As {@link stripUnauthorableLocal}, resolving the template's virtual fields itself.
 * For callers NOT inside a db transaction (getVirtualFields reads documentTemplates).
 */
export async function stripUnauthorableLocalForMerge(
  existingDoc: Pick<Document, 'super_form' | 'super_form_meta' | 'template_id'>,
  fieldPermissions: FieldPermissions | undefined
): Promise<{ superForm: Record<string, unknown> | undefined; meta: SuperFormCRDTMetadata | undefined }> {
  return stripUnauthorableLocal(existingDoc, await getVirtualFields(existingDoc.template_id), fieldPermissions);
}

/**
 * Prepare super_form data and CRDT metadata for sending to the API.
 * Filters metadata to only include fields present in superForm, converts
 * tel fields to E.164 format, strips virtual fields, and strips restricted
 * (non-viewable) sensitive fields so a masked/stale value is never written back.
 */
async function prepareApiFormData(
  templateId: number | undefined,
  superForm: Record<string, unknown> | undefined,
  meta: SuperFormCRDTMetadata | undefined,
  fieldPermissions: FieldPermissions | undefined
): Promise<{ apiSuperForm: Record<string, unknown> | undefined; filteredMeta: SuperFormCRDTMetadata }> {
  // Strip restricted fields up front so neither the payload nor its metadata
  // can carry a sensitive slug the syncing person may not view.
  const restricted = restrictedSlugs(fieldPermissions);
  const { superForm: viewableForm, meta: viewableMeta } = stripRestrictedFields(superForm, meta, restricted);

  const filteredMeta: SuperFormCRDTMetadata = {};
  if (viewableMeta) {
    if (viewableForm) {
      for (const fieldName of Object.keys(viewableForm)) {
        if (viewableMeta[fieldName] !== undefined) {
          filteredMeta[fieldName] = viewableMeta[fieldName];
        }
      }
    }
    // Prefill confirmations piggyback on super_form_meta under the
    // `confirmed_prefill:` namespace — those keys have no super_form companion,
    // so include them independently of superForm presence (kept outside the
    // superForm guard so a doc with only confirmations still ships them).
    for (const key of Object.keys(viewableMeta)) {
      if (key.startsWith('confirmed_prefill:')) {
        filteredMeta[key] = viewableMeta[key];
      }
    }
  }

  const telFields = await getTelFieldFormats(templateId);
  const virtualFields = await getVirtualFields(templateId);
  const apiSuperForm = viewableForm
    ? stripVirtualFields(convertTelFieldsToE164(viewableForm, telFields), virtualFields)
    : viewableForm;

  return { apiSuperForm, filteredMeta };
}

/**
 * Look up a document's template and return a map of tel field names to their
 * iMask format strings.
 */
async function getTelFieldFormats(templateId: number | undefined): Promise<Map<string, string>> {
  if (!templateId) return new Map();
  const template = await db.documentTemplates.get(templateId);
  if (!template?.super_form?.definition?.form) return new Map();
  return findTelFields(template.super_form.definition.form as Record<string, unknown>);
}

/**
 * Walk a template's form definition and collect the names of all fields
 * that should not be sent to the API: `virtual: true` (local-only computed
 * fields) and `disabled: true` (read-only display fields like computed_age).
 */
function findVirtualFields(form: Record<string, unknown>): Set<string> {
  const result = new Set<string>();
  const walk = (obj: Record<string, unknown>) => {
    for (const [key, val] of Object.entries(obj)) {
      if (!val || typeof val !== 'object') continue;
      const field = val as Record<string, unknown>;
      if (field.virtual === true || field.disabled === true) {
        result.add(key);
      }
      if (field.fields && typeof field.fields === 'object') {
        walk(field.fields as Record<string, unknown>);
      }
    }
  };
  walk(form);
  return result;
}

/**
 * Look up a document's template and return the set of virtual field names.
 */
export async function getVirtualFields(templateId: number | undefined): Promise<Set<string>> {
  if (!templateId) return new Set();
  const template = await db.documentTemplates.get(templateId);
  if (!template?.super_form?.definition?.form) return new Set();
  return findVirtualFields(template.super_form.definition.form as Record<string, unknown>);
}

/**
 * Strip virtual fields from a super_form data object.
 * Returns a new object without the virtual field keys.
 */
function stripVirtualFields(
  data: Record<string, unknown>,
  virtualFields: Set<string>
): Record<string, unknown> {
  if (virtualFields.size === 0) return data;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!virtualFields.has(key)) {
      result[key] = value;
    }
  }
  return result;
}

// Track documents currently being synced to prevent duplicate syncs
const syncingDocuments = new Set<number | string>();

// Track documents with pending auto-sync operations, and the timer handles that
// will fire them, so a scheduled auto-sync can be cancelled (see
// cancelPendingAutoSyncs). Without a handle the CRDT auto-sync setTimeout below
// was un-cancellable and would fire ~1s later even after the context that
// scheduled it was gone.
const pendingAutoSyncs = new Set<number | string>();
const autoSyncTimers = new Map<number | string, ReturnType<typeof setTimeout>>();

/**
 * Cancel every scheduled-but-not-yet-fired CRDT auto-sync. The auto-sync is a
 * deferred (1s) setTimeout; left pending it fires a syncDocument long after the
 * pull that scheduled it. In Cypress that timer leaks across the test boundary
 * and its now-unintercepted fetch flips the shared connectivity flag, so the
 * component support hook calls this on teardown. Safe to call anytime.
 */
export function cancelPendingAutoSyncs(): void {
  for (const timer of autoSyncTimers.values()) clearTimeout(timer);
  autoSyncTimers.clear();
  pendingAutoSyncs.clear();
}

// CRDT timing constants
const CRDT_AUTO_SYNC_DELAY_MS = 1000;  // Delay after CRDT merge before auto-sync
const PULL_DOCUMENT_TIMEOUT_MS = 10000; // Timeout for pulling document from server

// The refresh GC never prunes a row written after (refresh start − this margin):
// it may be a draft a concurrent sync promoted mid-refresh, and the margin
// absorbs server-vs-device clock drift in the row's updated_at. A genuinely
// deleted doc just survives one refresh cycle and is pruned by the next.
// Currently set to 5 seconds
const PRUNE_RECENT_WRITE_MARGIN_MS = 5 * 60 * 1000;

/**
 * Whether the document is unsynced or has a retryable sync error (permission/other).
 * Documents with sync_error_type 'deleted_online' are excluded — that's a terminal state.
 */
export function documentNeedsSync(doc: Document): boolean {
  // Terminal conflict — the document was completed online while these edits were pending,
  // so they can never be written back; never retry (even if synced is somehow false)
  if (doc.sync_error_type === 'completed_online') return false;
  return !doc.synced || doc.sync_error_type === 'permission' || doc.sync_error_type === 'other';
}

/** Key ordering is an implementation detail of whatever produced the object;
 *  sort it away so an identical payload always hashes identically. */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value !== null && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    return Object.keys(source).sort().reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = canonicalize(source[key]);
      return acc;
    }, {});
  }
  return value;
}

/** FNV-1a. `basis` varies so two passes give two independent 32-bit values. */
function fnv1a(input: string, basis: number): string {
  let hash = basis;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * A stable digest of everything that determines the document PATCH/POST body.
 * Used to recognise "this is byte-for-byte the payload the server already
 * refused" — see {@link isSyncBlocked}. Two hashes are concatenated because a
 * single 32-bit collision would silently hold a document that the user had in
 * fact edited.
 *
 * Deliberately narrower than the whole record: `updated_at`, `revision`, ETags
 * and the sync bookkeeping fields all move without changing what is sent.
 *
 * It hashes the inputs to the payload rather than the payload itself, because
 * building the payload is async (prepareApiFormData reads the template's
 * virtual fields). Two things can therefore change underneath it: a template
 * refresh, and — for a finalize hold — the captured signatures, which live in
 * the signatures table and are not part of this digest. A document held across
 * either is released by any edit or by Try again in Settings > Sync.
 */
export function syncPayloadFingerprint(doc: Document): string {
  const dismissals = pendingWarningDismissals(doc.warning_dismissals);
  const body = JSON.stringify(canonicalize({
    template_id: doc.template_id ?? null,
    super_form: doc.super_form ?? null,
    super_form_meta: doc.super_form_meta ?? null,
    field_permissions: doc.field_permissions ?? null,
    client_uuid: doc.client_uuid ?? null,
    client_id: doc.client_id ?? null,
    offline_uuid: doc.local_id ?? null,
    program_id: doc.program_id ?? null,
    organization_site_id: doc.site_id ?? null,
    recorded_at: doc.recorded_at ?? null,
    duplicated_from_id: doc.duplicated_from_id ?? null,
    // Only when there are any, so documents without dismissals keep their
    // existing fingerprints (and holds).
    ...(dismissals.length > 0 ? { warning_dismissals: dismissals } : {}),
  }));
  // The length is folded into the second pass: both hashes share an update
  // function whose low bits are dominated by the final code unit, so hashing
  // the same string twice with only a different basis is less independent than
  // it looks.
  return `${fnv1a(body, 0x811c9dc5)}${fnv1a(`${body.length}:${body}`, 0x01000193)}`;
}

/**
 * Whether this document is holding because the server already rejected exactly
 * this payload. Only a 422 sets the fingerprint: a validation verdict is
 * deterministic, so re-sending an unchanged body can only fail the same way,
 * and the sweep skips it until the local record changes (any edit, including
 * the fix) or the user asks for a retry explicitly. Without this a single bad
 * value produced one PATCH per sweep, indefinitely: 18 identical 422s over 23
 * hours in the case that prompted it. Every other 4xx/5xx — a 429, a proxy's
 * 400 — stays retryable exactly as before, because nothing about it promises
 * the next attempt will fail too.
 *
 * There is deliberately no clock on the hold. What releases it: any local
 * edit (the fingerprint changes), any server-side change to the document (the
 * refresh CRDT-merges it and the fingerprint changes), the user pressing Try
 * again (force), the next request being a different stage, or any success.
 * A held document is not silent — the sync banner and Settings › Sync both
 * report it — so the one case none of those cover, a server-side fix that
 * never touches the document, is a visible click away rather than something
 * to guess a retry interval for.
 *
 * NOTE: this is not `documentNeedsSync`. That predicate answers "is there
 * local work here worth protecting", and the refresh GC prunes rows where it
 * says no — a blocked document still has unsynced edits and must keep saying
 * yes, or its work would be deleted.
 */
export function isSyncBlocked(doc: Document): boolean {
  if (doc.sync_error_type !== 'other') return false;
  if (!doc.sync_error_fingerprint) return false;
  if (doc.sync_error_fingerprint !== syncPayloadFingerprint(doc)) return false;

  // Held per request, not per document. The hold records which request was
  // refused; it only applies if that is the request the document would send
  // next. A refused update must not stop a finalize — a different body the
  // server may accept — and this has to live in the predicate, not only in
  // syncDocument's guard, or the sweep drops the document from the batch
  // before syncDocument ever gets to make that distinction.
  return doc.sync_error_stage === nextSyncStage(doc);
}

/** Which request syncDocument will send for this document next. */
export function nextSyncStage(doc: Document): 'update' | 'finalize' {
  return doc.locally_finalized && !doc.signatures_synced ? 'finalize' : 'update';
}

/**
 * The server's own words for why it refused the document, preferring the
 * per-attribute errors hash ("recorded_at: cannot be more than 45 days in the
 * future") over the generic message. This string is what the Sync tab shows
 * and what rides along on the analytics event, so it is the only explanation
 * anyone gets — never reduce it to the status code when the server said more.
 */
function formatServerErrors(
  errorData: { message?: string; errors?: Record<string, string[] | string> | string[] },
  status: number
): string {
  // Two shapes in play: the offline API renders `document.errors` (attribute →
  // messages), while /finalize renders `errors: resource.errors.full_messages`
  // (a flat array, already prefixed with the attribute name). Assuming either
  // one throws on the other, inside a try/catch that would swallow the whole
  // error-persisting branch with it.
  const { errors } = errorData;
  if (Array.isArray(errors)) {
    if (errors.length > 0) return errors.join('; ');
  } else if (errors && typeof errors === 'object') {
    const entries = Object.entries(errors);
    if (entries.length > 0) {
      return entries
        .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
        .join('; ');
    }
  }
  return errorData.message ?? `Sync failed: ${status}`;
}

/**
 * A document with local edits waiting to be pushed. This is the one predicate
 * shared by the reconnect sweep, the client Documents tab, the dashboard
 * warning banner and Settings > Sync. (The sweep runs over every document on
 * the device; the counts below are scoped to one organization, so for a
 * multi-organization user they can be lower than what the sweep pushes.)
 */
export function isPendingDocument(doc: Document): boolean {
  // A row with no form data (undefined or null super_form) has nothing to push
  // and is neither attempted by the sweep nor counted.
  return !doc.synced && !!doc.super_form;
}

/**
 * Whether any of the organization's documents is pending. Stops at the first
 * hit, so when something is pending it is cheaper than countPendingDocuments;
 * when nothing is, it walks the same range. Sampled once per visible sync by
 * the banner — not a cheap thing to poll.
 */
export async function hasPendingDocuments(organizationId: number): Promise<boolean> {
  const clientIds = await db.clients.where('organization_id').equals(organizationId).primaryKeys();
  if (clientIds.length === 0) return false;
  const first = await db.documents
    .where('client_id')
    .anyOf(clientIds as number[])
    .and(isPendingDocument)
    .first();
  return first !== undefined;
}

/** Number of pending documents across the organization's clients. */
export async function countPendingDocuments(organizationId: number): Promise<number> {
  const clientIds = await db.clients.where('organization_id').equals(organizationId).primaryKeys();
  if (clientIds.length === 0) return 0;
  return db.documents
    .where('client_id')
    .anyOf(clientIds as number[])
    .and(isPendingDocument)
    .count();
}

/**
 * One document the server would not accept, ready to render. Built for
 * Settings > Sync, which is where `sync_error_message` is surfaced — before
 * this the message was written to Dexie on every failed sync and read by
 * nothing, so neither the assessor nor support could see why a document was
 * stuck without a CloudWatch dig.
 *
 * Carries the client's slug rather than their name: this is the support-facing
 * screen and the slug is what a server-side log search is keyed on. Note the
 * document name can still carry PHI (a document's own `name` is free text), so
 * this is "no client name", not "no PHI".
 */
export interface DocumentSyncErrorReport {
  documentId: number | string;
  documentName: string;
  clientId: number;
  clientSlug: string;
  /** The server's own words, verbatim as persisted. Humanize at render time. */
  message: string;
  syncErrorType: SyncErrorType;
  /** Holding on this payload — nothing will be retried until it changes. */
  blocked: boolean;
  /** Route to the document, so the fix is one click from the explanation. */
  path: string | null;
}

/** The states where local work is waiting and the server has said why it
 *  won't take it. `deleted_online` and `completed_online` are terminal and
 *  have their own resolution UI in the client's Documents tab, so listing them
 *  here would only offer a retry that cannot work. */
const SURFACED_SYNC_ERROR_TYPES: SyncErrorType[] = ['other', 'permission'];

/**
 * Every document in the organization whose last sync attempt left an
 * actionable explanation behind, by document name.
 */
export async function listDocumentSyncErrors(organizationId: number): Promise<DocumentSyncErrorReport[]> {
  // Indexed on sync_error_type (v43), so this walks only documents that have an
  // error rather than deserializing every row's super_form to filter them out.
  // This runs inside the tab's live query, which re-fires on any document write
  // — a sync sweep is exactly when it would otherwise churn hardest.
  const errored = await db.documents
    .where('sync_error_type')
    .anyOf(SURFACED_SYNC_ERROR_TYPES)
    // `!synced` makes the list self-cleaning: a row whose error state some path
    // forgot to clear drops out the moment the document actually syncs.
    .and(doc => !!doc.sync_error_message && !doc.synced)
    .toArray();
  if (errored.length === 0) return [];

  // Fetch only the clients the errored documents belong to — normally one or
  // two — rather than deserialising the whole caseload on every live-query run.
  // Still scoped to the organization, so a row from another org can't leak in.
  const clientIds = [...new Set(errored.map(doc => doc.client_id).filter((id): id is number => id != null))];
  const clients = (await db.clients.bulkGet(clientIds))
    .filter((client): client is NonNullable<typeof client> => !!client && client.organization_id === organizationId);
  if (clients.length === 0) return [];
  const slugById = new Map(clients.map(client => [client.id, client.label]));

  const documents = errored.filter(doc => doc.client_id != null && slugById.has(doc.client_id));
  if (documents.length === 0) return [];

  // Only ever a fallback for a document carrying neither label nor name, and
  // template definitions are large — fetch the few that need one, not all.
  const needsTemplateName = documents
    .filter(doc => !doc.template_label && !doc.name && doc.template_id != null)
    .map(doc => doc.template_id as number);
  const templates = needsTemplateName.length === 0
    ? []
    : (await db.documentTemplates.bulkGet([...new Set(needsTemplateName)])).filter((t): t is DocumentTemplate => !!t);
  const templateNameById = new Map(templates.map(template => [template.id, template.label || template.name]));

  return documents
    .map(doc => ({
      documentId: doc.id!,
      documentName: doc.template_label || doc.name || templateNameById.get(doc.template_id!) || 'Untitled',
      clientId: doc.client_id!,
      clientSlug: slugById.get(doc.client_id!)!,
      message: doc.sync_error_message!,
      syncErrorType: doc.sync_error_type!,
      blocked: isSyncBlocked(doc),
      path: documentPath(slugById.get(doc.client_id!)!, doc),
    }))
    .sort((a, b) => a.documentName.localeCompare(b.documentName));
}

const DELETED_ONLINE_MESSAGE = 'This document was deleted in the online app.';
const PERMISSION_ERROR_MESSAGE = 'You do not have permission to edit this document for the selected program/site.';

// Emit document.sync.success or document.sync.failure based on the final state
// of the document after a sync attempt. Called at every return path in
// syncDocument so the data team's failure-rate recipe sees every branch
// (404/403/other from the initial PATCH, 403 from finalize, the missing-
// client_id finalize bailout, and the locally-finalized signature error path).
// A `sync_error_type` means failure regardless of the `synced` flag, since
// `markDeletedOnline` leaves `synced=true` to stop auto-retry.
function trackSyncResult(doc: Document): void {
  const baseData = {
    document_id: doc.id,
    client_id: doc.client_id,
    template_id: doc.template_id,
    status: doc.status,
  };

  if (doc.sync_error_type) {
    trackEvent('document.sync.failure', {
      ...baseData,
      sync_error_type: doc.sync_error_type === 'completed_online' && isUnderReview(doc) ? 'in_review_online' : doc.sync_error_type,
      error_message: doc.sync_error_message ?? undefined,
    }).catch(err => logger.warn('Failed to track document.sync.failure', err));
  } else if (doc.synced) {
    trackEvent('document.sync.success', baseData)
      .catch(err => logger.warn('Failed to track document.sync.success', err));
  }
}

/**
 * Whether this rejection blocked a payload that wasn't already blocked. A
 * forced retry of an unchanged body recomputes the identical fingerprint, so
 * comparing against the row's previous one is what keeps the event to one per
 * distinct refused payload rather than one per attempt.
 */
function isNewlyBlocked(before: Document, after: Document): boolean {
  return !!after.sync_error_fingerprint
    && after.sync_error_fingerprint !== before.sync_error_fingerprint;
}

/**
 * Emitted once, at the moment a document starts holding on a payload the
 * server refused. `document.sync.failure` already carries the message, but it
 * fires on every attempt — this one fires exactly once per distinct rejected
 * payload, so the server has a clean record of how often a real person is
 * stuck and on what. The message is the server's own text (see the SECURITY
 * note on AnalyticsEventData.error_message).
 */
function trackSyncBlocked(doc: Document): void {
  trackEvent('document.sync.blocked', {
    document_id: doc.id,
    client_id: doc.client_id,
    template_id: doc.template_id,
    status: doc.status,
    sync_error_type: doc.sync_error_type ?? undefined,
    error_message: doc.sync_error_message ?? undefined,
  }).catch(err => logger.warn('Failed to track document.sync.blocked', err));
}

/**
 * Mark a document as deleted online and persist it. Returns the updated document.
 */
export async function markDeletedOnline(doc: Document): Promise<Document> {
  const deletedDoc: Document = {
    ...doc,
    synced: true,
    sync_error_type: 'deleted_online',
    sync_error_message: DELETED_ONLINE_MESSAGE,
    sync_error_fingerprint: null,
    sync_error_at: null,
    sync_error_stage: null,
    ...(doc.synced === false ? { deleted_online_had_unsynced_edits: true } : {}),
  };
  await db.documents.put(deletedDoc);
  return deletedDoc;
}

/**
 * Mark a document with a permission sync error and persist it.
 * @param synced — true (default) to stop auto-retry; false for initial sync failures that should retry after user fixes program/site.
 */
async function markPermissionError(doc: Document, serverMessage?: string, synced = true): Promise<Document> {
  const permissionDoc: Document = {
    ...doc,
    synced,
    sync_error_type: 'permission',
    sync_error_message: serverMessage ?? PERMISSION_ERROR_MESSAGE,
    sync_error_fingerprint: null,
    sync_error_at: null,
    sync_error_stage: null,
  };
  await db.documents.put(permissionDoc);
  return permissionDoc;
}

/**
 * Resolve a 409 document_completed conflict, which the server returns for every locked
 * status (completed, signing, in review). The server copy is authoritative
 * and the document can't be written to from here again, so adopt the server copy and stop retrying
 * (documentNeedsSync excludes the terminal 'completed_online' state):
 *   - no real differences between the local edits and the server copy → silently adopt, no error.
 *   - differences → keep the local edits (completed_online_local_super_form) for the resolution
 *     modal and flag the row 'completed_online' so the "Completed Online" chip shows.
 */
async function resolveLockedOnline(doc: Document, localSuperForm: Record<string, unknown> | undefined, serverDocument: Partial<Document> | undefined): Promise<Document> {
  // A partial 409 payload (no document, or a document without super_form) must not
  // wipe the row: fall back to the current super_form so nothing is lost and the
  // diff below doesn't flag every local field against an empty server copy.
  const serverProvidedSuperForm = serverDocument?.super_form as Record<string, unknown> | undefined;
  const serverSuperForm = (serverProvidedSuperForm ?? doc.super_form ?? {}) as Record<string, unknown>;
  // Only a real diff against the SERVER's copy can prove "no conflict". When the
  // 409 payload omits super_form we can't verify the local edits match the
  // (unknown) server copy, so we conservatively treat any local edits as a
  // conflict and preserve them for the resolution modal — otherwise they'd be
  // silently adopted as clean and overwritten (untracked) on the next pull.
  const changed = serverProvidedSuperForm
    ? calculateChangedFields(serverProvidedSuperForm, localSuperForm)
    : localSuperForm;
  const hasConflict = changed !== undefined && Object.keys(changed).length > 0;

  const resolved: Document = {
    ...doc,
    ...serverDocument,
    id: doc.id,
    local_id: doc.local_id,
    super_form: serverSuperForm,
    synced: true,
    locally_finalized: false,
    signatures_synced: false,
    sync_error_type: hasConflict ? 'completed_online' : null,
    sync_error_message: hasConflict ? lockedOnlineCopy({ status: serverDocument?.status ?? doc.status }).syncMessage : null,
    sync_error_fingerprint: null,
    sync_error_at: null,
    sync_error_stage: null,
    completed_online_local_super_form: hasConflict ? localSuperForm : undefined,
  };

  await db.documents.put(resolved);
  return resolved;
}

/**
 * Emit document.discarded for a row whose never-synced local values are being
 * dropped — by the user's explicit "Discard Edit" choice on a completed_online
 * conflict, implicitly when the refresh GC removes an unresolved
 * completed_online conflict, or when the user manually deletes a deleted_online
 * row that stranded unsynced edits (the GC keeps those for recovery, so manual
 * deletion is the only way their values go away).
 *
 * Records the fields and values the user gave up before the local edits go away.
 * discarded_values carries the client's form values — retained
 * deliberately so a discarded edit can be recovered/audited.
 *
 * For a completed_online conflict the discarded values are the kept local edits
 * that differ from the adopted server copy. For a deleted_online row there is no
 * server copy to diff against (the document is gone), so the whole local form is
 * reported.
 */
export function trackDocumentDiscarded(doc: Document): void {
  const discardedValues = doc.sync_error_type === 'deleted_online'
    ? ((doc.super_form ?? {}) as Record<string, unknown>)
    : calculateChangedFields(
        (doc.super_form ?? {}) as Record<string, unknown>,
        doc.completed_online_local_super_form,
      ) ?? {};
  trackEvent('document.discarded', {
    document_id: doc.id,
    template_id: doc.template_id,
    status: doc.status,
    discarded_values: discardedValues,
  }).catch(err => logger.warn('Failed to track document.discarded', err));
}

/**
 * Emit document.crdt_discarded when a CRDT merge threw away non-empty local
 * values (the server won those field conflicts). Persists the lost field→value
 * pairs as an offline analytics event so a discarded edit can be recovered or
 * audited — same rationale as document.discarded, but for automatic merges
 * rather than an explicit user discard.
 */
function trackCrdtDiscards(
  doc: Pick<Document, 'id' | 'template_id' | 'status'>,
  discarded: MergeResult['discarded'],
): void {
  if (discarded.length === 0) return;
  trackEvent('document.crdt_discarded', {
    document_id: doc.id,
    template_id: doc.template_id,
    status: doc.status,
    discarded_values: Object.fromEntries(discarded.map(({ field, value }) => [field, value])),
  }).catch(err => logger.warn('Failed to track document.crdt_discarded', err));
}

/**
 * React to a live broadcast revealing the document was completed or sent for
 * review elsewhere (the payload's server-computed `locked` flag). Status deliberately never
 * flows through the WebSocket merge (status changes come from explicit user
 * actions), so this is the explicit path:
 *   - unsynced local edits → resolve the locked-online conflict (adopts the
 *     server copy, keeps the differing edits for the resolution modal);
 *   - clean document → pull the authoritative copy so the editor flips
 *     read-only through the normal status-adoption path.
 * A pending local finalize wins — its replay will reconcile with the server.
 */
export async function handleRemoteLock(clientId: string | number, documentId: number | string, serverDocument?: Partial<Document>): Promise<void> {
  const doc = await db.documents.get(documentId);
  if (!doc) return;
  if (doc.locally_finalized) return;
  if (isDocumentLocked(doc)) return;

  if (doc.synced === false) {
    await resolveLockedOnline(doc, doc.super_form as Record<string, unknown> | undefined, serverDocument);
  } else {
    await pullDocument(clientId, Number(documentId));
  }
}

/**
 * Seed a new local draft from the local edits kept on a completed_online
 * conflict (reuses the duplicate-prefill machinery), clear the conflict on
 * the original, and return the new draft's local_id for navigation. Shared
 * by the documents list and the in-editor conflict modal.
 */
export async function createDraftFromLockedOnlineConflict(
  doc: Document,
  template: DocumentTemplate | undefined,
  clientId: number,
  currentPerson: { id: number; official_full_name?: string } | null | undefined,
): Promise<string> {
  const localEdits = doc.completed_online_local_super_form ?? {};
  const now = new Date().toISOString();
  const uuid = globalThis.crypto.randomUUID();
  const localId = `draft-${uuid}`;
  // Same mirror contract as the Duplicate action: the snapshot stands in for
  // the live person the server reads when re-deriving core-data slugs on sync.
  const clientRecord = await db.clients.get(clientId);
  const duplicatePrefills = buildDuplicatePrefills(
    localEdits,
    template?.super_form?.definition as SuperFormDefinition | undefined,
    { name: doc.name, completedAt: doc.completed_at },
    template,
    clientRecord?.core_data_snapshot,
  );

  await db.documents.add({
    id: uuid,
    local_id: localId,
    name: doc.name,
    client_id: clientId,
    template_id: doc.template_id,
    template_label: doc.template_label,
    status: 'draft',
    super_form: { ...localEdits },
    ...(doc.field_permissions ? { field_permissions: doc.field_permissions } : {}),
    ...(Object.keys(duplicatePrefills).length ? { field_prefills: duplicatePrefills } : {}),
    created_at: now,
    updated_at: now,
    synced: false,
    ...(doc.program_id ? { program_id: doc.program_id } : {}),
    ...(doc.site_id ? { site_id: doc.site_id } : {}),
    ...(currentPerson ? {
      creator_id: currentPerson.id,
      creator: { id: currentPerson.id, full_name: currentPerson.official_full_name ?? '' },
    } : {}),
  });

  // The original keeps the completed server copy; clear its conflict state.
  if (doc.id != null) await discardLockedOnlineConflict(doc.id);
  return localId;
}

/**
 * Resolve a completed_online conflict by discarding the local edits and keeping the
 * server copy (already stored as the document's super_form). Clears the conflict state
 * so the "Completed Online" chip disappears.
 */
export async function discardLockedOnlineConflict(documentId: number | string): Promise<void> {
  const doc = await db.documents.get(documentId);
  if (!doc) return;

  trackDocumentDiscarded(doc);

  delete doc.completed_online_local_super_form;
  doc.sync_error_type = null;
  doc.sync_error_message = null;
  await db.documents.put(doc);
}

/**
 * Mark a document as unsynced so that syncDocument will pick it up.
 */
export async function markDocumentUnsynced(documentId: number | string): Promise<void> {
  const doc = await db.documents.get(documentId);
  if (doc && doc.synced !== false) {
    await db.documents.update(documentId, { synced: false });
  }
}


/**
 * Calculate which fields have actually changed between old and new super_form data
 * Returns only the changed fields, not the entire form
 *
 * Handles null/undefined values properly:
 * - value -> null: included (field was cleared)
 * - null -> value: included (field was set)
 * - null -> null: not included (no change)
 * - undefined -> undefined: not included (no change)
 */
function calculateChangedFields(
  oldData: Record<string, unknown> | undefined,
  newData: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!newData) return undefined;
  if (!oldData) return newData;

  const changes: Record<string, unknown> = {};

  // Check each field in the new data
  for (const [key, newValue] of Object.entries(newData)) {
    // Skip undefined values - they get stripped by JSON.stringify and would
    // result in empty super_form payloads (super_form: {}) being sent to server
    if (newValue === undefined) continue;

    const oldValue = oldData[key];

    // Compare values using JSON.stringify to handle all types including null
    // This ensures null values are included when they differ from the old value
    if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
      changes[key] = newValue;
    }
  }

  return Object.keys(changes).length > 0 ? changes : undefined;
}

/**
 * Sync all contacts referenced in a document's super_form.
 * Throws if any contact fails to sync, preventing the document from being
 * sent to the server with unresolvable UUID references.
 *
 * @param superForm The document's super_form data
 * @returns The super_form with UUID contact IDs replaced by server IDs
 */
async function syncReferencedContacts(
  superForm: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const contactIds = extractContactIds(superForm);
  if (contactIds.length === 0) return superForm;

  const unsyncedContacts = await getUnsyncedContactsByIds(contactIds);

  if (unsyncedContacts.length > 0) {
    // Sync new contacts (POST) before updates (PATCH) so that server IDs
    // are available when we replace UUIDs in the super_form.
    const newContacts = unsyncedContacts.filter(c => Boolean(c.local_id && !c.synced));
    const updatedContacts = unsyncedContacts.filter(c => !(c.local_id && !c.synced));

    logger.debug(`Syncing referenced contacts before document sync: ${newContacts.length} new, ${updatedContacts.length} updated`);

    const failedIds: (number | string)[] = [];

    // 1. Create new contacts first (POST)
    for (const contact of newContacts) {
      try {
        await syncContact(contact);
      } catch {
        failedIds.push(contact.id);
      }
    }

    // 2. Then update existing contacts (PATCH)
    for (const contact of updatedContacts) {
      try {
        await syncContact(contact);
      } catch {
        failedIds.push(contact.id);
      }
    }

    if (failedIds.length > 0) {
      throw new Error(
        `Cannot sync document: failed to sync referenced contacts [${failedIds.join(', ')}]`
      );
    }
  }

  // Always replace UUID IDs with server IDs.
  // This covers both freshly-synced contacts AND previously-synced contacts
  // whose UUIDs may still be in the super_form (e.g. if the React form state
  // wasn't updated after a prior sync replaced the IDs in IndexedDB).
  const updatedSuperForm = await replaceContactIdsInSuperForm(superForm, contactIds);

  return updatedSuperForm;
}

/**
 * Interface for document sync callbacks
 */
interface SyncDocumentCallbacks {
  onStart?: () => void;
  onSuccess?: (document: Document) => void;
  onError?: (message: string) => void;
}

// Shape of the offline API's document payloads (create / update / finalize /
// create_as_finalized responses all render the same document partial).
interface ServerDocumentResponse {
  id?: number;
  status?: string;
  revision?: string;
  updated_at?: string;
  recorded_at?: string;
  super_form?: Record<string, unknown>;
  super_form_meta?: SuperFormCRDTMetadata;
  field_prefills?: Document['field_prefills'];
  field_permissions?: Document['field_permissions'];
  program_id?: number;
  site_id?: number;
  organization_site_id?: number;
  signatures?: Signature[];
  warning_dismissals?: unknown;
}

// Merge a server document payload into the local record. The server owns
// status and revision; site may arrive as `organization_site_id` or `site_id`.
// `sentDismissals` are the warning dismissals the request carried: the server
// accepted them, so they stop being pending.
function mergeServerDocumentResponse(
  document: Document,
  responseData: ServerDocumentResponse,
  updatedSuperForm: Document['super_form'],
  sentDismissals: WarningDismissalUpload[] = [],
): Document {
  return {
    ...document,
    id: responseData.id ?? document.id, // Update ID to server ID
    local_id: document.local_id, // Preserve local_id for draft URL lookups
    status: responseData.status ?? document.status,
    revision: responseData.revision ?? document.revision,
    updated_at: responseData.updated_at ?? new Date().toISOString(),
    recorded_at: responseData.recorded_at ?? document.recorded_at,
    super_form: responseData.super_form ?? updatedSuperForm,
    super_form_meta: responseData.super_form_meta ?? document.super_form_meta,
    field_prefills: responseData.field_prefills ?? document.field_prefills,
    field_permissions: responseData.field_permissions ?? document.field_permissions,
    program_id: responseData.program_id ?? document.program_id,
    site_id: responseData.site_id ?? responseData.organization_site_id ?? document.site_id,
    warning_dismissals: acknowledgeWarningDismissals(
      document.warning_dismissals, sentDismissals, serverWarningDismissals(responseData.warning_dismissals),
    ),
    synced: true,
    sync_error_type: null,
    sync_error_message: null,
    sync_error_fingerprint: null,
    sync_error_at: null,
    sync_error_stage: null,
  };
}

// A draft received its server id: replace the local record and re-key its
// signature rows — both their document_id and their document-scoped local_id.
async function replaceDraftRecord(oldId: number | string, updatedDocument: Document): Promise<void> {
  await db.transaction('rw', db.documents, db.signatures, async () => {
    // A dismissal recorded on the draft while its create was in flight.
    const draft = await db.documents.get(oldId);
    if (draft?.warning_dismissals?.length) {
      updatedDocument.warning_dismissals = mergeWarningDismissals(draft.warning_dismissals, updatedDocument.warning_dismissals);
      if (updatedDocument.warning_dismissals.some((item) => item.pending)) updatedDocument.synced = false;
    }
    await db.documents.delete(oldId);
    await db.documents.add(updatedDocument);

    const oldSignatures = await db.signatures.where('document_id').equals(oldId).toArray();
    if (oldSignatures.length > 0) {
      await db.signatures.where('document_id').equals(oldId).delete();
      await db.signatures.bulkPut(oldSignatures.map(sig => ({
        ...sig,
        document_id: updatedDocument.id!,
        local_id: scopedLocalId(updatedDocument.id!, slotIdFromLocalId(sig.local_id))
      })));
    }
  });
}

/**
 * Syncs an offline document to the server
 *
 * @param document The document to sync
 * @param callbacks Optional callbacks for different stages of the process
 * @param options `force` re-sends a payload the server already rejected — for
 *   a retry the user asked for, never for an automatic sweep.
 * @returns Promise with the updated document after sync
 */
export async function syncDocument(
  document: Document,
  callbacks?: SyncDocumentCallbacks,
  options?: { force?: boolean }
): Promise<Document | null> {
  // Snapshot of the current person used to attribute the document.sync.*
  // event on the 401 path — fetchWithAuth wipes current_person before
  // throwing AuthExpiredError, so by the time the catch fires the session-
  // keyed lookup inside trackEvent would return null and silently drop the
  // row (the bucket the data team most wants to see). Captured lazily
  // below, after the cheap guards return early without needing it.
  let personSnapshot: Awaited<ReturnType<typeof getCurrentPerson>> | undefined;

  // Idempotent emit guard. trackSyncResult is called at every happy-path
  // return; the catch also emits document.sync.failure on exceptions. If
  // any future code between trackSyncResult and the function return throws
  // (e.g. an added cleanup step), the catch would emit a second event for
  // the same logical attempt — emitSyncResult collapses that to one.
  let resultEmitted = false;
  const emitSyncResult = (doc: Document): void => {
    if (resultEmitted) return;
    resultEmitted = true;
    trackSyncResult(doc);
  };

  try {
    // Guard: document.id must be defined for sync operations
    if (document.id === undefined) {
      throw new Error('Cannot sync document without an id');
    }

    // Check if this document is already being synced
    if (syncingDocuments.has(document.id!)) {
      logger.debug(`Document ${document.id} is already being synced, skipping duplicate sync`);
      return document;
    }

    // Add to syncing set
    syncingDocuments.add(document.id!);

    if (callbacks?.onStart) callbacks.onStart();

    // Check if we need to finalize (locally_finalized but signatures not synced)
    const needsFinalize = document.locally_finalized && !document.signatures_synced;

    // Check if already synced and doesn't need finalize
    if (document.synced && !needsFinalize) {
      if (callbacks?.onSuccess) callbacks.onSuccess(document);
      syncingDocuments.delete(document.id!);
      return document;
    }

    // Check if we're online. Offline is the steady state for this PWA, not
    // a failure — emitting document.sync.failure here would dominate the
    // data team's failure-rate dashboard (autosave, the data event listener,
    // and the runtime orchestrator all call syncDocument unconditionally).
    // Early-return through the onError callback path so callers still get
    // the user-facing signal, and mark resultEmitted to suppress the catch's
    // failure emit.
    if (!isOnline()) {
      syncingDocuments.delete(document.id!);
      if (callbacks?.onError) callbacks.onError('Cannot sync while offline');
      resultEmitted = true;
      return null;
    }

    // The server already refused this exact payload. Re-sending it can only
    // reproduce the same rejection, so hold until the local record changes or
    // the user retries explicitly. Not a failure — the outcome was recorded
    // (document.sync.failure + document.sync.blocked) when the rejection
    // happened, and emitting again on every sweep would bury the real signal.
    // Held per request, not per document: a refused update must not stop a
    // finalize (a different body the server may accept), and a refused finalize
    // must not be replayed on every sweep — which it was, because exempting
    // every needsFinalize document from the hold left that loop wide open.
    // isSyncBlocked is already stage-aware (see nextSyncStage), so this is the
    // same decision the sweep made when it selected the batch.
    if (!options?.force && isSyncBlocked(document)) {
      logger.debug(`Document ${document.id} is holding on a rejected payload; not re-sending`);
      syncingDocuments.delete(document.id!);
      if (callbacks?.onError && document.sync_error_message) callbacks.onError(document.sync_error_message);
      resultEmitted = true;
      return document;
    }

    // Past the cheap guards — now we actually need the person. Capture
    // BEFORE fetchWithAuth runs so a 401 doesn't poison the snapshot.
    personSnapshot = await getCurrentPerson();
    const currentPerson = personSnapshot;
    if (!currentPerson) {
      throw new Error('No current person selected - cannot sync document');
    }

    // Sync any referenced contacts before sending the document.
    // Throws if any contact fails — the outer catch handles cleanup.
    let updatedSuperForm = document.super_form;
    if (document.super_form) {
      updatedSuperForm = await syncReferencedContacts(document.super_form);

      // Persist replaced IDs locally so a retry doesn't re-send UUIDs
      if (updatedSuperForm !== document.super_form) {
        await db.documents.update(document.id!, { super_form: updatedSuperForm });
      }
    }

    // Track the current document state (may be updated during sync)
    let updatedDocument: Document = { ...document, super_form: updatedSuperForm };

    // Determine if this is a draft that has never been synced to the server.
    // Gate on the id TYPE, not just local_id presence: local_id is preserved
    // forever after replaceDraftRecord promotes a draft to its numeric server
    // id (draft URL lookups depend on it), and offline re-edits set
    // synced:false on that promoted row — `local_id && !synced` alone would
    // re-classify it as new and POST a duplicate create (or route an offline
    // re-finalize to create_as_finalized, duplicating a completed document).
    // Matches updateDocument's `existsOnServer = typeof id === 'number'`.
    const isNewDraft = Boolean(document.local_id && !document.synced && typeof document.id === 'string');

    // Guard against duplicate document creation. On reconnect several sync
    // sweeps fire at once (window 'online' + 'server-reachable', the client
    // DocumentsTab dispatch, the mount auto-sync) and each hands us the same
    // stale unsynced snapshot. The in-memory syncingDocuments set only covers a
    // single in-flight call, so a sweep that runs just after an earlier one
    // finished — and released the guard — would POST a second create for the
    // same draft. Re-read the authoritative local record by its stable local_id
    // (preserved when replaceDraftRecord swaps the draft UUID for the server id)
    // and bail if this draft was already created: synced for a plain create, or
    // locked/completed for a create-as-finalized. The server also dedupes on
    // offline_uuid, but stopping the redundant request here is the real fix.
    if (isNewDraft && document.local_id) {
      const persisted = await db.documents.where('local_id').equals(document.local_id).first();
      // "Already created" means the persisted row was re-keyed to the server id
      // (replaceDraftRecord swaps the draft UUID for it on create success) or is
      // marked synced. Locked status is deliberately NOT a creation signal: a
      // draft finalized offline is locally 'completed' before the server has
      // ever seen it, and treating that as created would skip the
      // create_as_finalized replay entirely.
      const alreadyCreated = persisted && (persisted.id !== document.id || persisted.synced === true);
      if (alreadyCreated) {
        logger.info(`Draft ${document.local_id} was already created by a concurrent sync; skipping duplicate create`);
        syncingDocuments.delete(document.id!);
        if (callbacks?.onSuccess) callbacks.onSuccess(persisted!);
        resultEmitted = true;
        return persisted!;
      }
    }

    // Only sync if not already synced (avoid redundant PATCH calls).
    // Skip the update entirely when we need to finalize — existing server docs
    // use the finalize endpoint directly, and new drafts use create_as_finalized.
    if (!document.synced && !needsFinalize) {
      // Safety check: ensure we have valid IDs before making API request
      if (!document.client_id || (!document.local_id && !document.id)) {
        const errorContext = {
          client_id: document.client_id,
          id: document.id,
          local_id: document.local_id,
          template_id: document.template_id,
          status: document.status
        };
        logger.error('Cannot sync document: missing client_id or id', errorContext);

        syncingDocuments.delete(document.id!);
        throw new Error('Cannot sync document: missing client_id or document id');
      }

      // Prepare the payload for the API
      // Backend accepts flattened format: person.assessments_need_scores.#{score_type}.#{slug}.#{field}
      // Include CRDT metadata for conflict-free merging
      const { apiSuperForm, filteredMeta } = await prepareApiFormData(
        document.template_id, updatedSuperForm, document.super_form_meta, document.field_permissions
      );
      const sentDismissals = pendingWarningDismissals(document.warning_dismissals);

      const payload = {
        document: {
          template_id: document.template_id,
          super_form: apiSuperForm,
          super_form_meta: filteredMeta,
          client_uuid: document.client_uuid,
          // Stable per-draft id so the server create is idempotent — a duplicate
          // or retried POST for this draft returns the existing document instead
          // of spawning a second one. Ignored by PATCH (existing docs).
          offline_uuid: document.local_id,
          program_id: document.program_id,
          organization_site_id: document.site_id,
          recorded_at: document.recorded_at,
          duplicated_from_id: document.duplicated_from_id,
          warning_dismissals_attributes: sentDismissals,
        },
      };

      // Determine if this is a create or update operation
      // Draft that has never been synced needs POST, otherwise PATCH
      const method = isNewDraft ? 'POST' : 'PATCH';
      const url = isNewDraft
        ? `${API_ENDPOINTS.clients}/${document.client_id}/documents?current_person_id=${currentPerson.id}`
        : `${API_ENDPOINTS.clients}/${document.client_id}/documents/${document.id}?current_person_id=${currentPerson.id}`;

      // Make the API call to sync the document
      const response = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
        timeout: 20000 // 20 second timeout for document sync
      }, () => {
        // Auth expired during sync — soft cleanup that preserves the local
        // DB. The document and its unsynced edits are still on disk and
        // will be picked up by the next sync attempt after re-auth.
        softSignOut();
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { message?: string; error?: string; errors?: Record<string, string[]> };

        if (response.status === 404) {
          const deletedDoc = await markDeletedOnline({ ...document, super_form: updatedSuperForm });
          syncingDocuments.delete(document.id!);
          emitSyncResult(deletedDoc);
          if (callbacks?.onError) callbacks.onError(deletedDoc.sync_error_message!);
          return deletedDoc;
        }

        if (response.status === 403) {
          const permissionDoc = await markPermissionError({ ...document, super_form: updatedSuperForm }, errorData.message, false);
          syncingDocuments.delete(document.id!);
          emitSyncResult(permissionDoc);
          if (callbacks?.onError) callbacks.onError(permissionDoc.sync_error_message!);
          return permissionDoc;
        }

        // The document was completed or sent for review online while these edits were pending.
        // Adopt the server copy and stop retrying; surface a conflict if the local edits differ.
        if (response.status === 409 && errorData.error === 'document_completed') {
          const conflictData = errorData as { document?: Partial<Document> };
          const resolvedDoc = await resolveLockedOnline({ ...document, super_form: updatedSuperForm }, updatedSuperForm, conflictData.document);
          syncingDocuments.delete(document.id!);
          emitSyncResult(resolvedDoc);
          if (resolvedDoc.sync_error_message && callbacks?.onError) callbacks.onError(resolvedDoc.sync_error_message);
          return resolvedDoc;
        }

        // Validation or other server error — mark document with sync error.
        // synced stays false: the edits were not accepted and must survive the
        // next CRDT-merging pull. The fingerprint of the refused payload rides
        // along so the sweep stops re-sending it (see isSyncBlocked).
        const errorMessages = formatServerErrors(errorData, response.status);
        const errorDoc: Document = {
          ...document,
          super_form: updatedSuperForm,
          synced: false,
          sync_error_type: 'other',
          sync_error_message: errorMessages,
          sync_error_fingerprint: null,
          sync_error_at: null,
          sync_error_stage: null,
        };
        if (response.status === 422) {
          errorDoc.sync_error_fingerprint = syncPayloadFingerprint(errorDoc);
          errorDoc.sync_error_at = new Date().toISOString();
          errorDoc.sync_error_stage = 'update';
        }
        await db.documents.put(errorDoc);
        syncingDocuments.delete(document.id!);
        emitSyncResult(errorDoc);
        if (isNewlyBlocked(document, errorDoc)) trackSyncBlocked(errorDoc);
        if (callbacks?.onError) callbacks.onError(errorMessages);
        return errorDoc;
      }

      const responseData = await response.json() as ServerDocumentResponse;

      // Update the document with sync information and server response
      // (includes CRDT merge result if server performed merge)
      updatedDocument = mergeServerDocumentResponse(document, responseData, updatedSuperForm, sentDismissals);

      // If we got a new ID from the server (for new drafts), delete old record and create new one
      if (responseData.id && document.id !== responseData.id && isNewDraft) {
        await replaceDraftRecord(document.id!, updatedDocument);
      } else {
        // An editor save may have written newer local edits to this row while
        // the PATCH was in flight; this response was built from the stale
        // sweep-time snapshot. Re-read and overlay any field that changed
        // locally since we sent, so a concurrent edit isn't clobbered by
        // synced:true. When nothing changed concurrently this is a plain put.
        const sentForm = (updatedSuperForm || {}) as Record<string, unknown>;
        const serverResponseDoc = updatedDocument;
        updatedDocument = await db.transaction('rw', db.documents, async () => {
          const current = await db.documents.get(serverResponseDoc.id!);
          // Same for a warning dismissal recorded while the request was in flight.
          serverResponseDoc.warning_dismissals = mergeWarningDismissals(current?.warning_dismissals, serverResponseDoc.warning_dismissals);
          if (serverResponseDoc.warning_dismissals.some((item) => item.pending)) serverResponseDoc.synced = false;
          const concurrentEdits = current?.super_form
            ? calculateChangedFields(sentForm, current.super_form as Record<string, unknown>)
            : undefined;
          if (!concurrentEdits) {
            await db.documents.put(serverResponseDoc);
            return serverResponseDoc;
          }
          const mergedMeta: SuperFormCRDTMetadata = { ...(serverResponseDoc.super_form_meta || {}) };
          for (const key of Object.keys(concurrentEdits)) {
            const stamp = current?.super_form_meta?.[key];
            if (stamp) mergedMeta[key] = stamp;
          }
          const merged: Document = {
            ...serverResponseDoc,
            super_form: { ...(serverResponseDoc.super_form || {}), ...concurrentEdits },
            super_form_meta: mergedMeta,
            synced: false, // concurrent edits still need to be pushed
          };
          await db.documents.put(merged);
          return merged;
        });
      }

      // Prefetch PDF for offline support
      prefetchPdf(updatedDocument);
    }

    // Handle signature sync for locally finalized documents
    if (needsFinalize) {
      // Safety check: ensure we have a client_id
      if (!document.client_id) {
        const errorContext = {
          client_id: document.client_id,
          document_id: document.id,
          template_id: document.template_id,
          context: 'syncDocument_finalize'
        };
        logger.error('Cannot finalize: missing client_id', errorContext);

        // The initial PATCH may have succeeded leaving updatedDocument.synced=true,
        // but finalize was skipped — record that as a sync failure so the data
        // team's recipe doesn't silently log this as a clean success.
        const missingClientIdDoc: Document = {
          ...updatedDocument,
          synced: false,
          sync_error_type: 'other',
          sync_error_message: 'Cannot finalize: missing client_id',
          // A local bailout, not a server verdict — never held.
          sync_error_fingerprint: null,
          sync_error_at: null,
          sync_error_stage: null,
        };
        await db.documents.put(missingClientIdDoc);
        syncingDocuments.delete(document.id!);
        emitSyncResult(missingClientIdDoc);
        return missingClientIdDoc;
      }

      try {
        // Prepare form data for the API
        const { apiSuperForm: apiFinalizeSuperForm, filteredMeta: filteredFinalizeMeta } =
          await prepareApiFormData(document.template_id, updatedSuperForm, document.super_form_meta, document.field_permissions);

        // One-shot signing: the signatures captured offline ride along with the
        // finalize, so the server completes the document in a single request and
        // the local status never bounces back to waiting_for_signature mid-sync.
        const capturedSignatures = (await getLocalSignatures(document.id!))
          .filter(sig => sig.local_completed && sig.status !== 'completed' && sig.name_confirmation && sig.signed_at && sig.signatory_id)
          .map(sig => ({
            // The wire carries the bare slot identity — the server matches it
            // against its own rows; the document scope is a local storage concern.
            local_id: slotIdFromLocalId(sig.local_id),
            name_confirmation: sig.name_confirmation,
            signed_at: sig.signed_at,
            signatory_id: sig.signatory_id,
            signature_format: sig.signature_format || 'typed',
            signing_request_id: sig.signing_request_id,
            client_signature_representative: sig.client_signature_representative,
            signing_statement: sig.signing_statement,
          }));

        // Dismissals still to upload ride along, so the server's Finish gate sees them.
        const sentFinalizeDismissals = pendingWarningDismissals(updatedDocument.warning_dismissals);

        // The finalize/create_as_finalized response payload (parsed once per branch)
        let finalizeData: { status?: string; signatures?: Signature[]; warning_dismissals?: unknown } | null = null;

        let finalizeResponse: Response;

        if (isNewDraft) {
          // New draft completed offline — create and finalize in a single call
          finalizeResponse = await fetchWithAuth(
            `${API_ENDPOINTS.clients}/${document.client_id}/documents/create_as_finalized?current_person_id=${currentPerson.id}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
              },
              body: JSON.stringify({
                document: {
                  template_id: document.template_id,
                  super_form: apiFinalizeSuperForm,
                  super_form_meta: filteredFinalizeMeta,
                  client_uuid: document.client_uuid,
                  // See the create payload — makes create_as_finalized idempotent
                  // so a retried finalize can't create a second completed document.
                  offline_uuid: document.local_id,
                  program_id: document.program_id,
                  organization_site_id: document.site_id,
                  recorded_at: dateCompletedValue(document),
                  duplicated_from_id: document.duplicated_from_id,
                  warning_dismissals_attributes: sentFinalizeDismissals,
                },
                signatures: capturedSignatures
              }),
              timeout: 60000
            },
            // Auth expired mid-finalize — soft cleanup, DB intact, the
            // finalize will be retried after re-auth.
            () => softSignOut()
          );

          if (finalizeResponse.ok) {
            const responseData = await finalizeResponse.json() as ServerDocumentResponse;
            finalizeData = responseData;

            // Update document with server ID and response data, then replace
            // the draft record with the server-ID record
            updatedDocument = mergeServerDocumentResponse(document, responseData, updatedSuperForm, sentFinalizeDismissals);
            await replaceDraftRecord(document.id!, updatedDocument);
          }
        } else {
          // Existing server document — finalize via PATCH
          const numericDocId = updatedDocument.id as number;

          finalizeResponse = await fetchWithAuth(
            `${MONAMI_HOST}/api/offline/clients/${document.client_id}/documents/${numericDocId}/finalize?current_person_id=${currentPerson.id}`,
            {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                document: {
                  super_form: apiFinalizeSuperForm,
                  super_form_meta: filteredFinalizeMeta,
                  recorded_at: dateCompletedValue(document),
                  warning_dismissals_attributes: sentFinalizeDismissals,
                },
                signatures: capturedSignatures
              }),
              timeout: 30000
            },
            // Auth expired mid-finalize — soft cleanup, DB intact, the
            // finalize will be retried after re-auth.
            () => softSignOut()
          );
        }

        if (finalizeResponse.status === 409) {
          const conflictData = await finalizeResponse.json().catch(() => ({})) as { error?: string; document?: Partial<Document> };

          // Document locked online while this finalize was pending
          if (conflictData.error === 'document_completed') {
            const resolvedDoc = await resolveLockedOnline(updatedDocument, updatedSuperForm, conflictData.document);
            syncingDocuments.delete(document.id!);
            emitSyncResult(resolvedDoc);
            if (resolvedDoc.sync_error_message && callbacks?.onError) callbacks.onError(resolvedDoc.sync_error_message);
            return resolvedDoc;
          }

          // The document was re-opened online, invalidating the ceremony our captured
          // signatures belong to. Re-finishing is refused by the server with 409. Drop the
          // stale captures (tracked via document.signatures_discarded) and adopt the re-opened
          // server copy — it's synced and re-opened, so there's nothing left to sync.
          if (conflictData.error === 'ceremony_invalidated') {
            await deleteLocalSignatures(document.id!);
            const reopenedDoc: Document = {
              ...updatedDocument,
              ...conflictData.document,
              id: updatedDocument.id,
              local_id: updatedDocument.local_id,
              synced: true,
              locally_finalized: false,
              signatures_synced: false,
              // Re-opened and synced — an error carried in from before is stale
              // and would list a healthy document as "could not accept".
              sync_error_type: null,
              sync_error_message: null,
              sync_error_fingerprint: null,
              sync_error_at: null,
              sync_error_stage: null,
            };
            await db.documents.put(reopenedDoc);
            syncingDocuments.delete(document.id!);
            emitSyncResult(reopenedDoc);
            return reopenedDoc;
          }
        }

        // Handle permission denied on finalize
        if (finalizeResponse.status === 403) {
          const errorData = await finalizeResponse.json().catch(() => ({})) as { message?: string };
          const permissionDoc: Document = {
            ...updatedDocument,
            synced: false,
            sync_error_type: 'permission',
            sync_error_message: errorData.message ?? 'You do not have permission to finalize this document for the selected program/site.',
            sync_error_fingerprint: null,
            sync_error_at: null,
            sync_error_stage: null,
          };
          await db.documents.put(permissionDoc);
          syncingDocuments.delete(document.id!);
          emitSyncResult(permissionDoc);
          if (callbacks?.onError) callbacks.onError(permissionDoc.sync_error_message!);
          return permissionDoc;
        }

        if (finalizeResponse.ok) {
          // Finalize succeeded — any errors from a prior failed replay are now
          // resolved, so clear the persisted banner and field marks.
          updatedDocument.external_errors = undefined;
          updatedDocument.finalize_field_errors = undefined;
          updatedDocument.finalize_warnings = undefined;
          const serverDocumentId = updatedDocument.id;
          const numericDocId = serverDocumentId as number;
          const template = await db.documentTemplates.get(document.template_id);

          // The draft branch parsed the response above; parse it here for the
          // finalize PATCH branch. The one-shot response carries the final
          // document status and its signature slots (completed when the
          // captured signatures were accepted).
          if (!finalizeData) {
            finalizeData = await finalizeResponse.json().catch(() => null);
            if (finalizeData?.status) {
              updatedDocument.status = finalizeData.status;
            }
            updatedDocument.warning_dismissals = acknowledgeWarningDismissals(
              updatedDocument.warning_dismissals, sentFinalizeDismissals, serverWarningDismissals(finalizeData?.warning_dismissals),
            );
            // The server accepted the finalize (form data included) — without
            // this the doc stays "Unsynced" and every sweep replays the finalize.
            updatedDocument.synced = true;
            updatedDocument.sync_error_type = null;
            updatedDocument.sync_error_message = null;
          }

          if (finalizeData?.status === 'in_review') {
            // The server sent the document to review without applying the captures sent with it
            await deleteLocalSignatures(updatedDocument.id!);
            updatedDocument.locally_finalized = false;
            updatedDocument.signatures_synced = false;
            await db.documents.put(updatedDocument);
          } else if (template?.signature_config) {
            const serverSignatures = finalizeData?.signatures?.length
              ? finalizeData.signatures
              : await fetchSignatures(document.client_id, numericDocId);

            // Merge the server rows into the local table. Slots the one-shot
            // completed now carry their name_confirmation; slots it rejected
            // stay locally captured (per-slot merge) for the retry below.
            await storeServerSignatures(updatedDocument.id!, serverSignatures);

            // Fallback: individually re-submit any slot the server still has as
            // requested but that was captured locally (e.g. rejected one-shot rows).
            const localSignatures = await getLocalSignatures(updatedDocument.id!);
            const signatureSyncErrors: string[] = [];
            let retried = false;
            for (const serverSig of serverSignatures) {
              if (serverSig.status === 'completed' || !serverSig.id) continue;

              // Match server row → local capture by slot identity (local rows
              // are stored under document-scoped keys, the wire uses bare slots)
              const serverSlotId = (serverSig.local_id && slotIdFromLocalId(serverSig.local_id)) || getSignatureSlotId(serverSig);
              const localSig = localSignatures.find(l => slotIdFromLocalId(l.local_id) === serverSlotId && l.local_completed);
              if (!localSig?.name_confirmation || !localSig.signed_at || !localSig.signatory_id) continue;

              // A capture taken against an earlier ceremony (the document was
              // re-opened and re-finalized since) must not sign the current one
              if (localSig.signing_request_id && serverSig.signing_request_id && localSig.signing_request_id !== serverSig.signing_request_id) continue;

              try {
                retried = true;
                const submitted = await submitSignature(
                  document.client_id,
                  numericDocId,
                  serverSig.id,
                  {
                    name_confirmation: localSig.name_confirmation,
                    signed_at: localSig.signed_at,
                    signatory_id: localSig.signatory_id,
                    signature_format: localSig.signature_format || 'typed',
                    client_signature_representative: localSig.client_signature_representative,
                    signing_statement: localSig.signing_statement
                  }
                );
                await db.signatures.put({ ...localSig, ...submitted, local_id: localSig.local_id, document_id: updatedDocument.id! });
              } catch (syncError) {
                const errorMsg = syncError instanceof Error ? syncError.message : 'Unknown error';
                const sigLabel = getSignatureLabel(serverSig);
                signatureSyncErrors.push(`${sigLabel}: ${errorMsg}`);
                logger.error(`Failed to sync signature ${serverSig.id}:`, syncError);
              }
            }

            if (signatureSyncErrors.length > 0) {
              logger.error('Signature sync had failures:', signatureSyncErrors);
              // Store sync errors on the document for later display/retry
              updatedDocument.signature_sync_errors = signatureSyncErrors;
            }

            // Mark signatures synced and persist BEFORE the re-fetch. The
            // per-slot submits above succeeded, so the signatures ARE synced;
            // pullDocument's pending-finalize guard keys on the persisted
            // `signatures_synced`, and if it still read false it would preserve
            // the stale pre-signature status (waiting_for_signature) instead of
            // adopting the server's post-signature status (e.g. completed). This
            // ordering was the source of a flaky finalize status.
            updatedDocument.signatures_synced = true;
            await db.documents.put(updatedDocument);

            // The one-shot response already carried the final status; re-fetch
            // only when individual retries may have transitioned the document.
            if (retried && signatureSyncErrors.length === 0) {
              const refreshedDoc = await pullDocument(document.client_id, numericDocId);
              if (refreshedDoc?.status) {
                updatedDocument.status = refreshedDoc.status;
                await db.documents.put(updatedDocument);
              }
            }
          } else {
            // No signatures required — finalize is complete, clear the flag
            updatedDocument.locally_finalized = false;
            await db.documents.put(updatedDocument);
          }
        } else {
          logger.error('Failed to finalize document on server:', finalizeResponse.status);
          const errorData = await finalizeResponse.json().catch(() => ({})) as {
            errors?: Record<string, string[]>;
            message?: string;
            external_errors?: unknown;
            super_form?: { errors?: ServerFieldErrors };
            warnings?: unknown;
          };
          const errorFields = errorData.errors ? Object.keys(errorData.errors) : [];
          const isProgramSiteError = errorFields.length > 0 && errorFields.every(f => ['program', 'organization_site_id', 'organization_site'].includes(f));
          updatedDocument.sync_error_type = isProgramSiteError ? 'permission' : 'other';
          // Same treatment as the update path: the attribute-level errors are
          // already parsed above, and "Finalize failed: 422" tells nobody which
          // field to fix — the problem this whole change exists to remove.
          updatedDocument.sync_error_message = formatServerErrors(errorData, finalizeResponse.status);
          // A refused finalize is as deterministic as a refused update; hold it
          // on its own terms so it is not replayed on every sweep forever.
          const heldBefore = updatedDocument.sync_error_fingerprint ?? null;
          updatedDocument.sync_error_fingerprint = null;
          updatedDocument.sync_error_at = null;
          updatedDocument.sync_error_stage = null;
          if (finalizeResponse.status === 422 && !isProgramSiteError) {
            updatedDocument.sync_error_fingerprint = syncPayloadFingerprint(updatedDocument);
            updatedDocument.sync_error_at = new Date().toISOString();
            updatedDocument.sync_error_stage = 'finalize';
          }
          // Fix-elsewhere errors (e.g. the client record's missing last name) the
          // offline form can't resolve locally. Persist them so the editor can
          // surface the banner when the document is reopened — the offline path's
          // counterpart to the online finish path's in-memory handling.
          updatedDocument.external_errors = Array.isArray(errorData.external_errors)
            ? (errorData.external_errors as ExternalError[])
            : undefined;
          // On-form field errors the server rejected (local validation passed —
          // e.g. the CRDT merge combined values from another client). Persist
          // for the editor to mark the fields on reopen.
          const replayFieldErrors = errorData.super_form?.errors;
          updatedDocument.finalize_field_errors =
            replayFieldErrors && Object.keys(replayFieldErrors).length > 0 ? replayFieldErrors : undefined;
          // Open soft-validation warnings the server's Finish gate reported.
          updatedDocument.finalize_warnings = finalizeWarnings(errorData.warnings);
          updatedDocument.synced = false;
          await db.documents.put(updatedDocument);
          // Same once-per-refused-payload record as the update path; without it
          // the event was blind to every finalize hold.
          if (isNewlyBlocked({ ...updatedDocument, sync_error_fingerprint: heldBefore }, updatedDocument)) {
            trackSyncBlocked(updatedDocument);
          }
        }
      } catch (signatureError) {
        logger.error('Error syncing signatures:', signatureError);
        updatedDocument.sync_error_type = 'other';
        updatedDocument.sync_error_message = signatureError instanceof Error ? signatureError.message : 'Signature sync failed';
        // Not a server verdict on a payload — never held.
        updatedDocument.sync_error_fingerprint = null;
        updatedDocument.sync_error_at = null;
        updatedDocument.sync_error_stage = null;
        await db.documents.put(updatedDocument);
      }
    }

    if (callbacks?.onSuccess) callbacks.onSuccess(updatedDocument);

    // Remove from syncing set after successful sync
    syncingDocuments.delete(document.id!);
    // If ID changed, also remove the new ID from syncing set
    if (updatedDocument.id !== document.id) {
      syncingDocuments.delete(updatedDocument.id!);
    }

    // The locally-finalized / signature branches above may have set
    // sync_error_type and flipped synced back to false; trackSyncResult
    // routes those to document.sync.failure.
    emitSyncResult(updatedDocument);

    return updatedDocument;

  } catch (error) {
    // Remove from syncing set on error
    syncingDocuments.delete(document.id!);

    const isAuthExpired = error instanceof AuthExpiredError;
    const errorMessage = isAuthExpired
      ? 'Authentication expired. Please log in again.'
      : error instanceof Error ? error.message : 'Unknown error during sync';

    // Guard against double-emit: if the happy path already routed a
    // document.sync.* event before the throw landed, the analytics row
    // already exists and re-emitting from here would double-count the
    // failure rate.
    if (!resultEmitted) {
      resultEmitted = true;
      const failureData: AnalyticsEventData = {
        document_id: document.id,
        client_id: document.client_id,
        template_id: document.template_id,
        status: document.status,
        sync_error_type: isAuthExpired ? 'auth_expired' : 'exception',
        error_message: errorMessage,
      };
      // Use the auth-aware variant when we captured a person before
      // fetchWithAuth's 401 cleanup wiped current_person — guarantees the
      // auth_expired row survives instead of being dropped by trackEvent's
      // null-person short-circuit. personSnapshot is undefined when we
      // threw before capture (the cheap guards: no-id / already-syncing).
      // Those paths don't exercise auth, so the plain trackEvent is fine
      // for them — and if there's no current person either, dropping is
      // the correct outcome (no one to attribute to).
      const emit = personSnapshot
        ? trackEventWithPerson('document.sync.failure', failureData, {
            personId: personSnapshot.id,
            organizationId: personSnapshot.organization.id,
          })
        : trackEvent('document.sync.failure', failureData);
      emit.catch(err => logger.warn('Failed to track document.sync.failure', err));
    }

    if (isAuthExpired) {
      logger.error(`Auth expired while syncing document ${document.id}`);
      if (callbacks?.onError) callbacks.onError(errorMessage);
      return null;
    }

    logger.error(`Error syncing document ${document.id}:`, error);

    if (callbacks?.onError) callbacks.onError(errorMessage);
    return null;
  }
}

/**
 * Syncs multiple documents in sequence
 *
 * @param documents Array of documents to sync
 * @param callbacks Optional callbacks for each document sync
 * @returns Promise with array of sync results
 */
export async function syncMultipleDocuments(
  documents: Document[],
  callbacks?: {
    onDocumentStart?: (document: Document) => void;
    onDocumentSuccess?: (document: Document) => void;
    onDocumentError?: (document: Document, message: string) => void;
    onComplete?: (results: { success: number; failed: number }) => void;
  },
  /** Passed straight to syncDocument. Selecting a held document for this batch
   *  is not enough on its own — syncDocument holds it again unless forced. */
  options?: { force?: boolean }
): Promise<Document[]> {
  const results: Document[] = [];
  let successCount = 0;
  let failedCount = 0;

  for (const document of documents) {
    if (callbacks?.onDocumentStart) {
      callbacks.onDocumentStart(document);
    }

    const result = await syncDocument(document, {
      onSuccess: (updatedDocument) => {
        successCount++;
        if (callbacks?.onDocumentSuccess) {
          callbacks.onDocumentSuccess(updatedDocument);
        }
      },
      onError: (message) => {
        failedCount++;
        if (callbacks?.onDocumentError) {
          callbacks.onDocumentError(document, message);
        }
      }
    }, options);

    if (result) {
      results.push(result);
    }
  }

  if (callbacks?.onComplete) {
    callbacks.onComplete({ success: successCount, failed: failedCount });
  }

  return results;
}

/**
 * Updates a document in the local database with CRDT support
 *
 * CRDT Migration Strategy:
 * - New documents: CRDT metadata created on first edit
 * - Existing documents: Metadata initialized using document.updated_at timestamp
 *   to ensure server data (with newer timestamps) wins over old local data
 * - Metadata is maintained for all fields in super_form
 * - Deleted fields have metadata cleaned up during sync
 *
 * Conflict Resolution:
 * - Uses Hybrid Logical Clock (HLC) timestamps for field-level Last-Write-Wins
 * - Local edits get new timestamps immediately
 * - Server conflicts resolved during pullDocument() or sync response
 * - Auto-sync triggered after merge if local wins exist
 *
 * @param documentId The ID of the document to update (number or UUID string for drafts)
 * @param updates Partial document data to update
 * @returns Promise with the updated document
 */
/**
 * Mark a prefilled field as confirmed by writing `confirmed_prefill:<slug>` into
 * super_form_meta with a fresh HLC timestamp. Idempotent — re-confirming bumps the
 * timestamp but the slug stays confirmed. There is no "unconfirm" pathway by design.
 */
export async function confirmPrefill(
  documentId: number | string,
  fieldName: string
): Promise<Document | null> {
  return confirmPrefills(documentId, [fieldName]);
}

/**
 * Seeds PARC prefills into a freshly-created draft from the server-provided
 * iCode prefill map on the cached client record (`client.icode_prefills`).
 * Matches each new field's `icode` (from its template definition) to the
 * server's most-recent value for that iCode and writes them into the draft as
 * `field_prefills` plus the carried-over values into `super_form`. Pending
 * PARC slugs are derived on read from `field_prefills.filter(requires_confirmation)`
 * minus the `confirmed_prefill:` keys in `super_form_meta`.
 *
 * Trade-off: iCodes from offline-only assessments the server hasn't processed
 * yet won't prefill until the client record syncs (we no longer reconstruct
 * the map from locally-cached prior documents).
 *
 * Returns the number of fields seeded (0 when the client has no iCode prefills
 * or no iCode mappings exist on the new template).
 */
export async function seedOfflineICodePrefills(
  documentId: number | string,
  clientId: number,
  newTemplate: { id: number | string; disable_prefills?: boolean; skip_prefill_confirmation?: boolean; super_form?: { definition?: { fields?: Record<string, { icode?: string } | null | undefined> } } | null }
): Promise<number> {
  // Templates can opt out of prefilling entirely (Document::Template#disable_prefills),
  // so assessors always start fresh. iCode seeding is the only client-side prefill
  // path (config-defined `prefill:` values are computed server-side only), so this
  // early return is the full offline suppression surface.
  if (newTemplate.disable_prefills) return 0;

  const newFields = newTemplate.super_form?.definition?.fields;
  if (!newFields) return 0;

  const hasIcodeFields = Object.values(newFields).some((def) => !!def?.icode);
  if (!hasIcodeFields) return 0;

  // 1) Read the server-provided iCode prefill map off the cached client.
  const client = await db.clients.get(clientId);
  const icodePrefills = client?.icode_prefills ?? {};
  if (Object.keys(icodePrefills).length === 0) return 0;

  // 2) Build the seed payload (pure).
  const { fieldPrefills, superFormSeeds } = buildSeedPayload(newFields, icodePrefills);
  const seededCount = Object.keys(fieldPrefills).length;
  if (seededCount === 0) return 0;

  // A template that skips prefill confirmation accepts the seeded values
  // as-is: keep the super_form value writes but emit no PARC metadata —
  // matching the server, whose field_prefills short-circuits to {}.
  const prefillsToWrite = newTemplate.skip_prefill_confirmation ? {} : fieldPrefills;

  // 3) Atomic write — only seed super_form values into empty slots.
  await db.transaction('rw', db.documents, async () => {
    const existing = await db.documents.get(documentId);
    if (!existing) return;
    const newSuperForm: Record<string, unknown> = { ...(existing.super_form as Record<string, unknown> || {}) };
    for (const [slug, value] of Object.entries(superFormSeeds)) {
      if (newSuperForm[slug] === undefined || newSuperForm[slug] === null || newSuperForm[slug] === '') {
        newSuperForm[slug] = value;
      }
    }
    await db.documents.update(documentId, {
      field_prefills: prefillsToWrite,
      super_form: newSuperForm as Document['super_form'],
      // Mark unsynced inline (rather than calling markDocumentUnsynced as a
      // second step) so the seed lands atomically with the form writes — same
      // pattern as every other compound update in this file.
      synced: false,
    });
  });

  return seededCount;
}

/**
 * Re-stamp fields that a depends_on edit just revealed (SuperForm's
 * onFieldsRevealed). Hiding a section is not an edit and bumps no timestamps,
 * so a re-revealed answer would otherwise compete in CRDT merges with the HLC
 * from when it was originally typed; revealing it re-asserts the answer the
 * user can now see, as of the gating edit. Blank policy (host-owned — only
 * this store's CRDT meta can tell the two blanks apart): a blank with no
 * per-field HLC stamp was never answered, so there is nothing to re-assert —
 * stamping it would let "no answer" clobber another device's real answer. A
 * stamped blank is a deliberate clear/uncheck the user is looking at and
 * competes like any answer. Wrapped in a Dexie 'rw' transaction for the same
 * read-modify-write race reason as confirmPrefills below.
 */
export async function stampRevealedFields(
  documentId: number | string,
  fieldNames: string[]
): Promise<Document | null> {
  if (fieldNames.length === 0) return null;
  const clientUUID = await getClientUUID();
  const hlc = getHLC(clientUUID);
  return db.transaction('rw', db.documents, async () => {
    const existing = await db.documents.get(documentId);
    if (!existing) return null;
    const superForm = (existing.super_form || {}) as Record<string, unknown>;
    const existingMeta: SuperFormCRDTMetadata = { ...(existing.super_form_meta || {}) };
    const newMeta: SuperFormCRDTMetadata = { ...existingMeta };
    let stamped = false;
    for (const fieldName of fieldNames) {
      if (isBlank(superForm[fieldName]) && !existingMeta[fieldName]) continue;
      newMeta[fieldName] = HybridLogicalClockService.toString(hlc.now());
      stamped = true;
    }
    if (!stamped) return existing;
    await db.documents.update(documentId, { super_form_meta: newMeta, synced: false });
    return { ...existing, super_form_meta: newMeta, synced: false };
  });
}

/**
 * Confirms one or more prefills atomically. Wrapped in a Dexie 'rw'
 * transaction so the read-modify-write of super_form_meta serializes against
 * any other concurrent confirm/update on the same document. Without the
 * transaction, two parallel confirms (e.g., blur-confirm on a select firing
 * at the same time as focus-confirm on the next text cell during Tab) both
 * read the same prior meta and the slower writer clobbers the faster one.
 */
export async function confirmPrefills(
  documentId: number | string,
  fieldNames: string[]
): Promise<Document | null> {
  if (fieldNames.length === 0) return null;
  const clientUUID = await getClientUUID();
  const hlc = getHLC(clientUUID);
  return db.transaction('rw', db.documents, async () => {
    const existing = await db.documents.get(documentId);
    if (!existing) return null;
    const superForm = (existing.super_form || {}) as Record<string, unknown>;
    const newMeta: SuperFormCRDTMetadata = { ...(existing.super_form_meta || {}) };
    for (const fieldName of fieldNames) {
      newMeta[`confirmed_prefill:${fieldName}`] = HybridLogicalClockService.toString(hlc.now());
      // Stamp a value HLC for the carried-over prefill value if it doesn't have
      // one yet. The seed (seedOfflineICodePrefills) writes the value into
      // super_form but no companion timestamp, so without this the server's CRDT
      // merge (Document::Crdt::Merge#merge_field) drops the field. A field the
      // user actually edited already has its own (newer) value HLC; leave it.
      const value = superForm[fieldName];
      const hasValue = value !== undefined && value !== null && value !== '';
      if (hasValue && newMeta[fieldName] === undefined) {
        newMeta[fieldName] = HybridLogicalClockService.toString(hlc.now());
      }
    }
    await db.documents.update(documentId, { super_form_meta: newMeta, synced: false });
    return { ...existing, super_form_meta: newMeta };
  });
}

/**
 * Records that the assessor keeps a soft-validation warning's answers as they
 * are, with their justification. Stamped from the device HLC like an answer
 * and kept pending until the server accepts it; the document goes unsynced so
 * the next sync sends it. Same read-modify-write transaction as confirmPrefills.
 * The warning's answers (`fields`) that carry no stamp yet are stamped just
 * before it: a stamp minted after the dismissal re-opens the warning, and the
 * merge's default stamps and confirmPrefills stamp an unstamped answer.
 */
export async function dismissWarning(
  documentId: number | string,
  key: string,
  justification: string,
  dismissedBy?: string,
  fields: string[] = []
): Promise<Document | null> {
  return writeWarningDismissal(documentId, key, fields, (_prior, changedHlc) => ({
    key,
    justification,
    dismissed_by: dismissedBy ?? null,
    undone: false,
    changed_hlc: changedHlc,
    pending: true,
  }));
}

/** Undoes an ignored warning (it must be justified again). */
export async function undismissWarning(documentId: number | string, key: string): Promise<Document | null> {
  return writeWarningDismissal(documentId, key, [], (prior, changedHlc) => (
    prior ? { ...prior, undone: true, changed_hlc: changedHlc, pending: true } : null
  ));
}

async function writeWarningDismissal(
  documentId: number | string,
  key: string,
  stampFields: string[],
  build: (prior: WarningDismissalRecord | undefined, changedHlc: string) => WarningDismissalRecord | null
): Promise<Document | null> {
  const clientUUID = await getClientUUID();
  const hlc = getHLC(clientUUID);
  return db.transaction('rw', db.documents, async () => {
    const existing = await db.documents.get(documentId);
    if (!existing) return null;
    // A locked document takes no writes, dismissals included.
    if (isDocumentLocked(existing)) return existing;
    const superForm = (existing.super_form || {}) as Record<string, unknown>;
    const meta: SuperFormCRDTMetadata = { ...(existing.super_form_meta || {}) };
    const unstamped = stampFields.filter((field) => !meta[field] && !isBlank(superForm[field]));
    for (const field of unstamped) meta[field] = HybridLogicalClockService.toString(hlc.now());
    const dismissals = existing.warning_dismissals ?? [];
    const item = build(dismissals.find((d) => d.key === key), HybridLogicalClockService.toString(hlc.now()));
    if (!item) return existing;
    const changes: Partial<Document> = {
      warning_dismissals: [...dismissals.filter((d) => d.key !== key), item],
      synced: false,
      ...(unstamped.length > 0 ? { super_form_meta: meta } : {}),
    };
    await db.documents.update(documentId, changes);
    return { ...existing, ...changes };
  });
}

export async function updateDocument(
  documentId: number | string,
  updates: Partial<Document>
): Promise<Document | null> {
  try {
    const existingDocument = await db.documents.get(documentId);
    if (!existingDocument) {
      throw new Error(`Document ${documentId} not found`);
    }

    // Keep track of only the changed fields for sending to server
    const changedFields = calculateChangedFields(
      existingDocument.super_form as Record<string, unknown>,
      updates.super_form as Record<string, unknown>
    );

    // Get client UUID and reuse cached HLC for CRDT metadata
    const clientUUID = await getClientUUID();
    const hlc = getHLC(clientUUID);

    // Create CRDT metadata for changed fields (simplified structure)
    let updatedMeta: SuperFormCRDTMetadata = { ...(existingDocument.super_form_meta || {}) };

    // If this is the first edit after CRDT deployment, initialize metadata for all existing fields
    // Use document's updated_at timestamp to preserve correct causality (not current time)
    if ((!existingDocument.super_form_meta || Object.keys(existingDocument.super_form_meta).length === 0) && existingDocument.super_form) {
      // Parse document's last updated time to use as base timestamp
      const docUpdatedTime = existingDocument.updated_at
        ? new Date(existingDocument.updated_at).getTime()
        : Date.now();

      // Create timestamp using document's last updated time
      // This ensures existing server data (with newer timestamps) wins over old local data
      const baseTimestamp = `${docUpdatedTime}-0-${clientUUID}`;

      for (const fieldName of Object.keys(existingDocument.super_form)) {
        updatedMeta[fieldName] = baseTimestamp;
      }

      // Advance HLC past the base timestamp so subsequent user edits always win.
      // If the server clock is ahead, docUpdatedTime could be in the future relative
      // to the local clock, causing hlc.now() to produce earlier timestamps.
      const baseHLC = HybridLogicalClockService.fromString(baseTimestamp);
      hlc.receive(baseHLC);

      logger.debug('Initialized CRDT metadata for legacy document', {
        documentId: existingDocument.id,
        fieldCount: Object.keys(existingDocument.super_form).length,
        baseTimestamp
      });
    }

    // Stamp changed fields with new timestamps (current time)
    if (changedFields) {
      for (const fieldName of Object.keys(changedFields)) {
        updatedMeta[fieldName] = HybridLogicalClockService.toString(hlc.now());
      }
    }

    // Callers (e.g. the prefill-confirmation handler) can pass explicit
    // super_form_meta entries to stamp namespaced keys like `confirmed_prefill:<slug>`.
    if (updates.super_form_meta) {
      for (const key of Object.keys(updates.super_form_meta)) {
        updatedMeta[key] = updates.super_form_meta[key];
      }
    }

    // Clean up metadata for fields that no longer exist in super_form.
    // Namespaced prefill keys live in meta without a super_form companion and
    // are preserved explicitly.
    const finalSuperForm = updates.super_form || existingDocument.super_form;
    if (finalSuperForm) {
      const validFields = new Set(Object.keys(finalSuperForm));
      const cleanedMeta: SuperFormCRDTMetadata = {};
      for (const fieldName of Object.keys(updatedMeta)) {
        if (validFields.has(fieldName) || fieldName.startsWith('confirmed_prefill:')) {
          cleanedMeta[fieldName] = updatedMeta[fieldName];
        }
      }
      updatedMeta = cleanedMeta;
    }

    // Always save locally first - merge full form data
    let updatedDocument: Document = {
      ...existingDocument,
      ...updates,
      updated_at: updates.updated_at || new Date().toISOString(),
      // Respect explicit synced value (e.g. from finalize), otherwise mark as unsynced
      synced: updates.synced !== undefined ? updates.synced : false,
      super_form_meta: updatedMeta,
      client_uuid: clientUUID
    };

    await db.documents.put(updatedDocument);

    // Sync any referenced contacts before sending the document to the server.
    // If contact sync fails, return the locally-saved document (synced: false)
    // so the document retries on the next sync cycle.
    if (isOnline() && updatedDocument.super_form) {
      try {
        const updatedSuperForm = await syncReferencedContacts(updatedDocument.super_form);

        if (updatedSuperForm !== updatedDocument.super_form) {
          // Also update the changed fields if they exist
          if (changedFields) {
            const contactIds = extractContactIds(updatedDocument.super_form);
            const updatedChangedFields = await replaceContactIdsInSuperForm(changedFields, contactIds);
            Object.assign(changedFields, updatedChangedFields);
          }

          updatedDocument = {
            ...updatedDocument,
            super_form: updatedSuperForm
          };

          await db.documents.put(updatedDocument);
        }
      } catch (contactSyncError) {
        logger.error('Contact sync failed, skipping server update:', contactSyncError);
        return updatedDocument;
      }
    }

    // If online and document exists on server (not a new unsaved draft), also update the server
    // A document exists on server if it has a numeric ID (drafts have UUID string IDs)
    // Completed documents are immutable on the server — skip the push to avoid a
    // 403 that would be misinterpreted as an auth failure and trigger logout.
    const existsOnServer = typeof existingDocument.id === 'number';
    // A pending local finalize must reach the server through syncDocument's
    // finalize/create_as_finalized path, never through update — the update
    // endpoint can't transition status (the server strips it), so a push here
    // would 200 and mask the pending replay behind synced: true.
    const pendingLocalFinalize = Boolean(
      (updates.locally_finalized ?? existingDocument.locally_finalized) &&
      !(updates.signatures_synced ?? existingDocument.signatures_synced)
    );
    if (isOnline() && existsOnServer && !updates.synced && !pendingLocalFinalize && existingDocument.status !== 'completed') {
      try {
        // Safety check: ensure we have valid IDs before making API request
        if (!existingDocument.client_id || !existingDocument.id) {
          const errorContext = {
            client_id: existingDocument.client_id,
            id: existingDocument.id,
            local_id: existingDocument.local_id,
            template_id: existingDocument.template_id,
            status: existingDocument.status,
            context: 'updateDocument'
          };
          logger.error('Cannot sync document: missing client_id or id', errorContext);

          return updatedDocument;
        }

        const currentPerson = await getCurrentPerson();

        if (currentPerson) {
          // Backend accepts flattened format: person.assessments_need_scores.#{score_type}.#{slug}.#{field}
          // Include CRDT metadata for conflict-free merging.
          // Status is never sent — the server only transitions status through
          // finalize/create_as_finalized and strips it from update params, so
          // sending it would misreport a status change as synced.

          // `changedFields` diffs the new form against the LOCAL copy, which
          // already contains any edits made while offline. If the document
          // still has unsynced edits (existingDocument.synced === false), those
          // accumulated fields are NOT in `changedFields`, so an incremental
          // PATCH would omit them — yet a 200 below marks the whole document
          // synced:true, permanently stranding every never-transmitted field.
          // When prior unsynced edits exist, send the FULL form + metadata so
          // the server receives everything (the CRDT merge is idempotent, so
          // re-sending already-synced fields is harmless); fall back to the
          // incremental diff only when the local copy is already in sync.
          // Use updatedDocument.super_form (post contact-sync) not finalSuperForm
          // (captured pre contact-sync), so the full-form flush carries server
          // contact IDs, not stale local UUIDs.
          const hasPendingUnsyncedEdits = existingDocument.synced === false;
          const superFormToSend = hasPendingUnsyncedEdits ? updatedDocument.super_form : changedFields;
          const sentDismissals = pendingWarningDismissals(updatedDocument.warning_dismissals);

          // Nothing to send only when the doc is already fully synced and this
          // call changed no fields (a lone recorded_at update still goes).
          if (!superFormToSend && updates.recorded_at === undefined && sentDismissals.length === 0) {
            logger.debug('No changes to sync, skipping server update');
            return updatedDocument;
          }

          const payload: {
            super_form?: Record<string, unknown>;
            super_form_meta?: SuperFormCRDTMetadata;
            client_uuid?: string;
            recorded_at?: string | null;
            warning_dismissals_attributes: WarningDismissalUpload[];
          } = {
            client_uuid: clientUUID,
            warning_dismissals_attributes: sentDismissals,
          };

          // Send the full form when flushing pending offline edits, otherwise
          // just the incremental diff. prepareApiFormData filters the metadata
          // to whatever form is passed.
          if (superFormToSend) {
            const { apiSuperForm, filteredMeta } = await prepareApiFormData(
              existingDocument.template_id, superFormToSend, updatedMeta, existingDocument.field_permissions
            );
            payload.super_form = apiSuperForm;
            payload.super_form_meta = filteredMeta;
          }

          if (updates.recorded_at !== undefined) {
            payload.recorded_at = updates.recorded_at;
          }

          const response = await fetchWithAuth(
            `${MONAMI_HOST}/api/offline/clients/${existingDocument.client_id}/documents/${existingDocument.id}?current_person_id=${currentPerson.id}`,
            {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                document: payload
              }),
              timeout: 5000 // 5 second timeout for updates
            },
            () => {
              // Auth expired mid-PATCH — soft cleanup; the unsynced changes
              // stay on disk and re-sync after re-auth.
              softSignOut();
            }
          );

          if (response.ok) {
            // Parse server response to get the updated document (includes new revision, merged data)
            const serverDoc = await response.json();

            // Update local document with server response (includes CRDT merge if applicable)
            const syncedDocument = {
              ...updatedDocument,
              revision: serverDoc.revision,
              updated_at: serverDoc.updated_at,
              super_form: serverDoc.super_form || updatedDocument.super_form,
              super_form_meta: serverDoc.super_form_meta || updatedDocument.super_form_meta,
              field_permissions: serverDoc.field_permissions ?? updatedDocument.field_permissions,
              synced: true,
              sync_error_type: null,
              sync_error_message: null,
              sync_error_fingerprint: null,
              sync_error_at: null,
              sync_error_stage: null,
            };
            // Re-read and overlay any field edited locally while this PATCH was in
            // flight — same in-flight race as syncDocument. Without this, an editor
            // save that landed during the request is clobbered by this response and
            // marked synced:true. When nothing changed concurrently it's a plain put.
            const sentForm = (updatedDocument.super_form || {}) as Record<string, unknown>;
            const reconciled = await db.transaction('rw', db.documents, async () => {
              const current = await db.documents.get(syncedDocument.id!);
              // The sent dismissals are accepted; one recorded mid-flight stays pending.
              syncedDocument.warning_dismissals = acknowledgeWarningDismissals(
                current?.warning_dismissals ?? updatedDocument.warning_dismissals,
                sentDismissals,
                serverWarningDismissals(serverDoc.warning_dismissals),
              );
              if (syncedDocument.warning_dismissals.some((item) => item.pending)) syncedDocument.synced = false;
              const concurrentEdits = current?.super_form
                ? calculateChangedFields(sentForm, current.super_form as Record<string, unknown>)
                : undefined;
              if (!concurrentEdits) {
                await db.documents.put(syncedDocument);
                return syncedDocument;
              }
              const mergedMeta: SuperFormCRDTMetadata = { ...(syncedDocument.super_form_meta || {}) };
              for (const key of Object.keys(concurrentEdits)) {
                const stamp = current?.super_form_meta?.[key];
                if (stamp) mergedMeta[key] = stamp;
              }
              const merged = {
                ...syncedDocument,
                super_form: { ...(syncedDocument.super_form || {}), ...concurrentEdits },
                super_form_meta: mergedMeta,
                synced: false, // concurrent edits still need to be pushed
              };
              await db.documents.put(merged);
              return merged;
            });
            return reconciled;
          } else if (response.status === 404) {
            return await markDeletedOnline(updatedDocument);
          } else if (response.status === 403) {
            const errorData = await response.json().catch(() => ({})) as { message?: string };
            // Keep the row unsynced (synced:false) so the un-acked local edits are
            // preserved by the next CRDT-merging pull and retried once the
            // program/site is corrected — matching syncDocument's 403 handling.
            return await markPermissionError(updatedDocument, errorData.message, false);
          } else if (response.status === 409) {
            // The document was completed elsewhere while this edit was being
            // made. Same terminal resolution as syncDocument's 409 branch:
            // adopt the server copy, keep any differing local edits for the
            // conflict modal, and stop retrying. Without this branch a raced
            // finalize was filed as a generic 'other' error with no way for
            // the editor to explain what happened.
            const errorData = await response.json().catch(() => ({})) as { error?: string; document?: Partial<Document> };
            if (errorData.error === 'document_completed') {
              return await resolveLockedOnline(
                updatedDocument,
                updatedDocument.super_form as Record<string, unknown> | undefined,
                errorData.document,
              );
            }
            logger.error('Failed to update document on server:', response.status);
            // Server rejected the PATCH — local edits were NOT accepted. Keep the
            // row unsynced so the next CRDT-merging pull preserves them and
            // syncDocument retries (instead of early-returning on synced:true).
            const conflictErrorDoc: Document = {
              ...updatedDocument,
              synced: false,
              sync_error_type: 'other',
              sync_error_message: `Update failed: ${response.status}`,
              // A conflict is not a verdict on the payload. Clearing this is
              // load-bearing, not tidiness: an earlier 422's fingerprint can
              // still match (a conflict need not move super_form), which would
              // block a retryable error as if it were a validation failure.
              sync_error_fingerprint: null,
              sync_error_at: null,
              sync_error_stage: null,
            };
            await db.documents.put(conflictErrorDoc);
            return conflictErrorDoc;
          } else {
            logger.error('Failed to update document on server:', response.status);
            // For other client errors (4xx) keep the row unsynced so the un-acked
            // edits survive the next pull and remain retryable.
            if (response.status >= 400 && response.status < 500) {
              // Parse the attribute-level errors too, not just `message` — a 422
              // on this path is the same class of rejection the sweep sees, and
              // "recorded_at: cannot be more than 45 days in the future" is the
              // only thing that lets anyone fix it.
              //
              // Deliberately NOT fingerprinted. syncPayloadFingerprint models the
              // full sync body; this PATCH sends a subset (changed fields, and
              // recorded_at only when this call set it). A 422 here can therefore
              // be about a field this request never carried — a bad recorded_at on
              // the server copy while the local one is good — and holding on that
              // would block the very sync that would have corrected it. The sweep
              // sends the whole body and fingerprints there, which is where the
              // repeated-PATCH problem lived.
              const errorData = await response.json().catch(() => ({})) as { message?: string; errors?: Record<string, string[]> };
              const errorDoc: Document = {
                ...updatedDocument,
                synced: false,
                sync_error_type: 'other',
                sync_error_message: formatServerErrors(errorData, response.status),
                sync_error_fingerprint: null,
                sync_error_at: null,
                sync_error_stage: null,
              };
              await db.documents.put(errorDoc);
              return errorDoc;
            }
          }
        }
      } catch (serverError) {
        if (serverError instanceof AuthExpiredError) {
          logger.error('Auth expired during server update');
          // Continue with local-only update - logout already handled by fetchWithAuth
        } else {
          logger.error('Server update failed:', serverError);
        }
        // Continue with local-only update
      }
    }

    return updatedDocument;
  } catch (error) {
    logger.error(`Error updating document ${documentId}:`, error);
    return null;
  }
}

/**
 * Finalize a document by calling the finalize member action on the Documents API
 * @param documentId The ID of the offline document (number or UUID string for drafts)
 * @param formValues The final form values
 * @returns The updated document
 */
export async function finalizeDocument(
  documentId: number | string,
  formValues: Record<string, unknown>
): Promise<Document | null> {
  try {
    // First update the form values locally
    const updatedDocument = await updateDocument(documentId, {
      super_form: formValues,
      updated_at: new Date().toISOString()
    });

    if (!updatedDocument) {
      throw new Error('Failed to update document');
    }

    // If we're online and document exists on server, call the finalize API.
    const existsOnServer = typeof updatedDocument.id === 'number';
    if (isOnline() && existsOnServer) {
      try {
        // Safety check: ensure we have valid IDs before making API request
        if (!updatedDocument.client_id || !updatedDocument.id) {
          throw new Error('Cannot finalize document: missing client_id or id');
        }

        // Get current person for organization scoping
        const currentPerson = await getCurrentPerson();
        if (!currentPerson) {
          throw new Error('No current person selected');
        }

        // Call the finalize member action on the Documents API
        // Prepare form data and CRDT metadata for the server
        const { apiSuperForm: apiFormValues, filteredMeta: filteredFinalizeMeta } =
          await prepareApiFormData(updatedDocument.template_id, formValues, updatedDocument.super_form_meta, updatedDocument.field_permissions);
        const sentDismissals = pendingWarningDismissals(updatedDocument.warning_dismissals);

        const response = await fetchWithAuth(
          `${MONAMI_HOST}/api/offline/clients/${updatedDocument.client_id}/documents/${updatedDocument.id}/finalize?current_person_id=${currentPerson.id}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              document: {
                super_form: apiFormValues,
                super_form_meta: filteredFinalizeMeta,
                // Finishing records the Date Completed the editor showed, even if never edited.
                recorded_at: dateCompletedValue(updatedDocument),
                warning_dismissals_attributes: sentDismissals,
              }
            }),
            // Finalization is 60s (vs 30s default) because it includes server-side
            // PDF generation and signature creation, which can be slow for large documents.
            timeout: 60000
          },
          () => {
            // Auth expired mid-finalize — soft cleanup, DB preserved.
            softSignOut();
          }
        );

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          const error = new Error(`Finalize API returned ${response.status}`);
          (error as Error & { validationErrors?: unknown; fieldErrors?: unknown }).validationErrors = errorBody?.errors;
          (error as Error & { fieldErrors?: unknown }).fieldErrors = errorBody?.super_form?.errors;
          // Errors on fields not rendered on this form (e.g. the client/person record).
          // Carry them through so the editor can point the user at the online app.
          (error as Error & { externalErrors?: unknown }).externalErrors = errorBody?.external_errors;
          // Open soft-validation warnings the server's Finish gate found.
          (error as Error & { warnings?: unknown }).warnings = finalizeWarnings(errorBody?.warnings);
          throw error;
        }

        // Parse the API response
        const apiResponse = await response.json();

        // The finalize response carries the signature slots the server just
        // created (setup_signatures!) — persist them so the Signature tab has
        // its rows without a separate fetch.
        await storeServerSignatures(documentId, apiResponse.signatures);

        // Update local document with the response data (includes CRDT merge if applicable)
        // Note: status is set by the server, not locally
        return await updateDocument(documentId, {
          status: apiResponse.status, // Only use server's status, don't set locally
          revision: apiResponse.revision,
          recorded_at: apiResponse.recorded_at ?? updatedDocument.recorded_at,
          super_form: apiResponse.super_form,
          super_form_meta: apiResponse.super_form_meta,
          synced: true,
          warning_dismissals: acknowledgeWarningDismissals(
            updatedDocument.warning_dismissals, sentDismissals, serverWarningDismissals(apiResponse.warning_dismissals),
          ),
          // Finalize succeeded — clear any errors persisted by a prior failed
          // sync-replay finalize.
          external_errors: undefined,
          finalize_field_errors: undefined,
          finalize_warnings: undefined,
          // The server accepted the document, so an earlier refusal and its hold
          // are stale. The pre-finalize push above normally clears these already;
          // stating it here keeps the invariant local to the success path rather
          // than resting on what that push happened to do.
          sync_error_type: null,
          sync_error_message: null,
          sync_error_fingerprint: null,
          sync_error_at: null,
          sync_error_stage: null,
          updated_at: apiResponse.updated_at || new Date().toISOString()
        });
      } catch (apiError) {
        if (apiError instanceof AuthExpiredError) {
          logger.error('Auth expired during finalization');
          // Don't attempt local finalization - user needs to re-authenticate
          throw apiError;
        } else {
          logger.error('Failed to finalize document on server:', apiError);
          // Don't set status locally - the server call failed
          // The document will remain unsynced and can be finalized later
          throw apiError;
        }
      }
    } else {
      // Offline or local draft - mark as locally finalized
      // Mark as locally finalized so the sync layer calls /finalize when back online.
      // A document headed for review skips signing, which the server would discard;
      // signature documents enter the signing window (the editor collects the
      // signatures into its Signature tab); everything else is set to 'completed'
      // so the UI reflects the user's intent immediately. The server reconciles
      // the final status when the sync layer replays this finalization online.
      logger.warn('Cannot finalize document while offline - marking as locally_finalized');
      const template = await db.documentTemplates.get(updatedDocument.template_id);
      const target = finishTarget(updatedDocument, template, await getCurrentPerson());
      let status: Document['status'] = 'completed';
      if (target === 'in_review') status = 'in_review';
      else if (template?.signature_config) status = 'waiting_for_signature';
      return await updateDocument(documentId, {
        locally_finalized: true,
        status,
        // The date the editor showed, kept as the finish recorded it.
        recorded_at: dateCompletedValue(updatedDocument),
        synced: false,
        updated_at: new Date().toISOString()
      });
    }
  } catch (error) {
    logger.error('Failed to finalize document:', error);
    throw error;
  }
}

// ========== PDF Operations ==========
// The following functions handle PDF fetching, caching, and management

// Edition PDFs are rendered asynchronously after a document is finalized. While
// a render is still in flight the PDF endpoint answers 202 Accepted (with a
// Retry-After hint) rather than the PDF body. Poll a bounded number of times
// before giving up and leaving the PDF un-cached for a later sync to retry.
const PDF_PENDING_STATUS = 202;
const PDF_PENDING_MAX_ATTEMPTS = 5;
const PDF_PENDING_DEFAULT_RETRY_MS = 5000;
// Both poll loops run on awaited, user-facing paths, so the server-controlled
// Retry-After must not be able to stall them for minutes: each individual wait
// is clamped, and the whole poll runs against a wall-clock deadline that
// bounds waits and request time together.
const PDF_PENDING_MAX_RETRY_MS = 30_000;
const PDF_PENDING_WAIT_BUDGET_MS = 45_000;

/** Why a render-poll stopped without the PDF: attempts/deadline ran out, or
 * connectivity dropped mid-poll. */
export type PdfPendingReason = 'exhausted' | 'offline';

/**
 * Thrown when a PDF is still rendering server-side after the bounded retries.
 * Lets callers branch on the pending state (`err instanceof PdfPendingError`,
 * plus `reason` for offline-specific copy) and render their own "still being
 * prepared" message instead of a generic not-found error — the user-facing
 * wording lives in the components.
 */
export class PdfPendingError extends Error {
  constructor(public readonly reason: PdfPendingReason = 'exhausted') {
    super(
      reason === 'offline'
        ? 'Device went offline while the PDF render was still pending'
        : 'PDF render is still pending after the bounded retries'
    );
    this.name = 'PdfPendingError';
  }
}

// Milliseconds to wait before re-requesting a pending PDF, from the server's
// Retry-After header when present and valid, else the default, clamped to the
// ceiling. Only the delta-seconds form of Retry-After is supported; the
// HTTP-date form parses to NaN and intentionally falls back to the default.
// NOTE: the PDF endpoint is cross-origin from this app, so headers.get()
// returns null — and the default applies — unless the server also sends
// Access-Control-Expose-Headers: Retry-After.
function pdfRetryDelayMs(response: Response): number {
  const header = response.headers.get('Retry-After');
  const seconds = header ? Number.parseInt(header, 10) : NaN;
  const ms = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : PDF_PENDING_DEFAULT_RETRY_MS;
  return Math.min(ms, PDF_PENDING_MAX_RETRY_MS);
}

/**
 * Timing seam for the render-poll, injected so tests can collapse the waits
 * (vi.spyOn(pdfPollTimers, ...)) against a virtual clock instead of patching
 * global setTimeout/performance — which would also hit unrelated timers.
 */
export const pdfPollTimers = {
  // Rejects with AbortError as soon as the signal aborts, so a caller that
  // navigated away doesn't sit out the remainder of a long render-poll wait.
  delay(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      const timeoutId = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      const onAbort = () => {
        clearTimeout(timeoutId);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  },
  now(): number {
    return performance.now();
  },
};

/**
 * Shared render-poll loop for both PDF fetch paths. Re-requests while the
 * server answers 202 (PDF render still in flight), waiting per Retry-After
 * between requests. `onPending` fires once, on the first 202, so callers can
 * surface "still being prepared" while the poll continues. Gives up when the
 * attempt cap or the wall-clock deadline is reached (the deadline bounds
 * request time as well as the waits, so a slow server can't stretch the poll
 * past the budget), or when connectivity drops mid-poll; the pending body is
 * never treated as the PDF. An abort on `signal` rejects with AbortError.
 */
async function pollWhilePending<T>(
  request: () => Promise<T>,
  getResponse: (result: T) => Response,
  options?: { maxAttempts?: number; signal?: AbortSignal; onPending?: () => void }
): Promise<{ ready: T } | { ready: null; gaveUp: PdfPendingReason }> {
  const maxAttempts = options?.maxAttempts ?? PDF_PENDING_MAX_ATTEMPTS;
  const deadline = pdfPollTimers.now() + PDF_PENDING_WAIT_BUDGET_MS;
  let result = await request();
  let attempt = 1; // requests made so far
  let pendingSignaled = false;
  while (getResponse(result).status === PDF_PENDING_STATUS) {
    if (!pendingSignaled) {
      pendingSignaled = true;
      options?.onPending?.();
    }
    const waitMs = pdfRetryDelayMs(getResponse(result));
    if (attempt >= maxAttempts || pdfPollTimers.now() + waitMs > deadline) {
      return { ready: null, gaveUp: 'exhausted' };
    }
    await pdfPollTimers.delay(waitMs, options?.signal);
    if (!isOnline()) return { ready: null, gaveUp: 'offline' };
    attempt += 1;
    result = await request();
  }
  return { ready: result };
}

/**
 * Prefetch PDF for offline support (non-blocking)
 * Only fetches if PDF link exists, not already cached, and online
 *
 * @param document The document to prefetch PDF for
 */
function prefetchPdf(document: Document): void {
  if (document.links?.pdf && !document.pdfContent && isOnline()) {
    fetchDocumentPdf(document).catch(err => {
      logger.debug('PDF prefetch failed (non-critical)', {
        documentId: document.id,
        error: err
      });
    });
  }
}

/**
 * Options (lifecycle callbacks + poll tuning) for fetchDocumentPdf
 */
interface FetchPdfOptions {
  onStart?: () => void;
  onComplete?: () => void;
  onError?: (message: string) => void;
  /**
   * How many requests to make while the server answers 202 (render pending).
   * Defaults to the full poll (PDF_PENDING_MAX_ATTEMPTS); bulk prefetch passes
   * 1 so a sweep over many pending PDFs doesn't poll each of them for minutes.
   */
  maxPendingAttempts?: number;
}

// Documents whose PDF fetch (and possible render-poll) is currently in flight,
// by document id. A concurrent call for the same document joins the existing
// fetch instead of stacking a duplicate poll loop against the same URL.
const pdfFetchesInFlight = new Map<NonNullable<Document['id']>, Promise<Document | null>>();

// Test hook: drop the in-flight bookkeeping so one test's hanging fetch can't
// make a later test join a promise that never settles.
export function clearPdfFetchesInFlight(): void {
  pdfFetchesInFlight.clear();
}

/**
 * Fetches a single PDF document and stores it in IndexedDB
 *
 * @param document The document to fetch the PDF for
 * @param options Optional callbacks and poll tuning. When a fetch for the
 *                same document is already in flight, the call resolves with
 *                that fetch's result and these callbacks do NOT fire — the
 *                original caller's do.
 * @returns Promise with the updated document including PDF content
 */
export async function fetchDocumentPdf(
  document: Document,
  options?: FetchPdfOptions
): Promise<Document | null> {
  if (!document.links?.pdf) return null;

  // Drafts without an id yet can't be deduped by key — let them fetch
  // directly rather than deduping unrelated documents against each other.
  if (document.id == null) return doFetchDocumentPdf(document, options);

  const inFlight = pdfFetchesInFlight.get(document.id);
  if (inFlight) return inFlight;

  const fetchPromise = doFetchDocumentPdf(document, options);
  pdfFetchesInFlight.set(document.id, fetchPromise);
  // doFetchDocumentPdf never rejects (its catch returns null), so this only
  // clears the slot once the fetch settles.
  void fetchPromise.finally(() => pdfFetchesInFlight.delete(document.id!));
  return fetchPromise;
}

async function doFetchDocumentPdf(
  document: Document,
  callbacks?: FetchPdfOptions
): Promise<Document | null> {
  if (!document.links?.pdf) return null;

  try {
    if (callbacks?.onStart) callbacks.onStart();

    // If we already have PDF content and it's up to date, return the document
    if (document.pdfContent) {
      const existingDoc = await db.documents.get(document.id!);
      if (existingDoc?.pdfContent &&
        new Date(existingDoc.updated_at).getTime() >= new Date(document.updated_at).getTime()) {
        if (callbacks?.onComplete) callbacks.onComplete();
        return existingDoc;
      }
    }

    // Get current person from Dexie for multi-org support
    const currentPerson = await getCurrentPerson();

    // Add current_person_id and engine to the signed S3 URL if available
    const urlWithAuth = new URL(document.links.pdf);
    if (currentPerson?.id) {
      urlWithAuth.searchParams.append('current_person_id', currentPerson.id.toString());
    }
    urlWithAuth.searchParams.append('engine', 'prawn');

    // Get existing document for conditional headers
    const existingDoc = await db.documents.get(document.id!);

    // Fetch the PDF content with conditional GET and timeout
    const fetchPdf = () => fetchFromApi(
      urlWithAuth.toString(),
      {
        storedETag: existingDoc?.pdfETag,
        storedLastModified: existingDoc?.pdfLastModified,
        authenticated: true,
        timeout: 30000, // 30 second timeout for PDF downloads
      },
    );

    // Poll while the server is still rendering the PDF (202). If the poll
    // gives up (attempts/budget exhausted, or connectivity dropped), leave
    // the PDF un-cached and return null — the document data has already
    // synced, so a later sync/prefetch will pick the PDF up. Giving up is a
    // non-error completion: onComplete still fires so loading states keyed
    // to it resolve.
    const polled = await pollWhilePending(fetchPdf, (r) => r.response, {
      maxAttempts: callbacks?.maxPendingAttempts,
    });
    if (polled.ready === null) {
      logger.debug('PDF render-poll gave up; leaving un-cached', {
        documentId: document.id,
        reason: polled.gaveUp,
      });
      if (callbacks?.onComplete) callbacks.onComplete();
      return null;
    }
    const { response, notModified, eTag, lastModified } = polled.ready;

    // Handle 304 Not Modified - content hasn't changed
    if (notModified) {
      if (callbacks?.onComplete) callbacks.onComplete();
      return existingDoc ?? document;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.status}`);
    }

    const blob = await response.blob();
    const pdfContent = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data);
      };

      reader.onerror = () => reject(new Error('Failed to read PDF data'));

      reader.readAsDataURL(blob);
    });

    // Update the document with the PDF content and conditional headers
    const updatedDocument = {
      ...document,
      pdfContent,
      pdfETag: eTag ?? undefined,
      pdfLastModified: lastModified ?? undefined
    };

    // Store in IndexedDB. update() (not put) so only the PDF fields are
    // written: this download may settle long after it started, and a
    // whole-row put of the in-memory document would clobber concurrent edits
    // — or resurrect a row the refresh GC deleted while the fetch was in
    // flight. update() on a since-deleted id is a no-op.
    try {
      if (document.id != null) {
        await db.documents.update(document.id, {
          pdfContent,
          pdfETag: eTag ?? undefined,
          pdfLastModified: lastModified ?? undefined,
        });
      }
    } catch (error) {
      logger.error('Error storing PDF in IndexedDB:', error);
    }

    // Call the completion callback
    if (callbacks?.onComplete) callbacks.onComplete();

    return updatedDocument;
  } catch (error) {
    if (error instanceof AuthExpiredError) {
      const errorMessage = 'Authentication expired. Please log in again.';
      logger.error(`Auth expired while fetching PDF for document ${document.id}`);
      if (callbacks?.onError) callbacks.onError(errorMessage);
      return null;
    }

    const errorMessage = error instanceof Error ? error.message : 'Unknown error fetching PDF';
    logger.error(`Error fetching PDF for document ${document.id}:`, error);

    if (callbacks?.onError) callbacks.onError(errorMessage);
    return null;
  }
}

/**
 * Gets PDF content for display in a modal with offline-first approach
 * 
 * @param documentId Optional document ID to lookup in IndexedDB
 * @param pdfUrl Optional PDF URL to fetch if not found offline
 * @param options `signal`: aborting cancels the fetch and any render-poll
 *                wait (rejecting with AbortError), so navigating away or
 *                retrying doesn't leave a poll loop running. `onPending`:
 *                fires once on the first 202, so the caller can show "still
 *                being prepared" while the poll continues underneath.
 * @returns Promise with base64 PDF content or null if not found
 */
export async function getPdfContent(
  documentId?: number,
  pdfUrl?: string,
  options?: { signal?: AbortSignal; onPending?: () => void }
): Promise<string | null> {
  // First try to get from offline storage if we have a document ID
  if (documentId !== undefined && documentId > 0) {
    try {
      const document = await db.documents.get(documentId);
      if (document?.pdfContent) {
        return document.pdfContent;
      }
    } catch (err) {
      logger.error('Failed to get PDF from IndexedDB:', err);
      // Continue to try fetching from URL
    }
  }

  // If not found offline and we have a URL, fetch it
  if (pdfUrl && isOnline()) {
    try {
      // Get current person from Dexie for multi-org support
      const currentPerson = await getCurrentPerson();

      // Add current_person_id and engine to the signed S3 URL if available
      const urlWithAuth = new URL(pdfUrl);
      if (currentPerson?.id) {
        urlWithAuth.searchParams.append('current_person_id', currentPerson.id.toString());
      }
      urlWithAuth.searchParams.append('engine', 'prawn');

      const fetchPdf = () => fetchWithAuth(urlWithAuth.toString(), {
        timeout: 30000, // 30 second timeout for PDF downloads
        signal: options?.signal,
      }, () => {
        // Auth expired during sync — soft cleanup that preserves the local
        // DB. The document and its unsynced edits are still on disk and
        // will be picked up by the next sync attempt after re-auth.
        softSignOut();
      });

      // Poll while the server is still rendering the PDF (202, honoring
      // Retry-After). If the poll gives up (attempts/deadline exhausted, or
      // connectivity dropped — the reason rides on the error), throw
      // PdfPendingError rather than caching a pending body, so the caller can
      // render a "still being prepared" state instead of a generic failure.
      const polled = await pollWhilePending(fetchPdf, (r) => r, {
        signal: options?.signal,
        onPending: options?.onPending,
      });
      if (polled.ready === null) throw new PdfPendingError(polled.gaveUp);
      const response = polled.ready;

      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }

      const blob = await response.blob();
      const base64data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read PDF data'));
        reader.readAsDataURL(blob);
      });

      // Store in IndexedDB for offline use if we have a document ID
      if (documentId !== undefined && documentId > 0 && base64data) {
        try {
          await db.documents.update(documentId, { pdfContent: base64data });
        } catch (err) {
          logger.error('Failed to cache PDF in IndexedDB:', err);
          // Don't fail the request if caching fails
        }
      }

      return base64data;
    } catch (err) {
      if (err instanceof AuthExpiredError) {
        logger.error('Auth expired while fetching PDF');
        throw new Error('Authentication expired. Please log in again.');
      }
      if (err instanceof PdfPendingError) {
        // Not a failure — the render just hasn't finished yet.
        logger.debug('PDF still rendering after retries', { reason: err.reason });
        throw err;
      }
      if (err instanceof DOMException && err.name === 'AbortError') {
        // The caller navigated away or retried — expected, keep it out of the
        // error logs.
        throw err;
      }
      logger.error('Failed to fetch PDF:', err);
      throw err;
    }
  }

  return null;
}

/**
 * Prefetch PDFs for a batch of documents in a controlled manner
 * 
 * @param documents List of documents to prefetch PDFs for
 * @param maxConcurrent Maximum number of concurrent fetches
 * @param onProgress Optional progress callback (0-100)
 * @returns Promise with updated documents including PDF content
 */
export async function prefetchDocumentPdfs(
  documents: Document[],
  maxConcurrent: number = 3,
  onProgress?: (progress: number) => void
): Promise<Document[]> {
  // Only fetch for documents that have PDF links and don't already have content
  const docsWithPdfLinks = documents.filter(doc => Boolean(doc.links?.pdf) && !doc.pdfContent);

  if (docsWithPdfLinks.length === 0) return documents;

  // Create a copy of the documents array to update with PDF content
  const updatedDocs = [...documents];
  let completedCount = 0;

  // Process in batches
  for (let i = 0; i < docsWithPdfLinks.length; i += maxConcurrent) {
    const batch = docsWithPdfLinks.slice(i, i + maxConcurrent);

    // Fetch PDFs in parallel for this batch. The bulk sweep never render-polls
    // a pending (202) PDF: with many uncached documents, polling each batch
    // would stretch the sweep into minutes of background requests. Still-
    // rendering PDFs stay un-cached and a later sync/prefetch picks them up.
    await Promise.allSettled(batch.map(async (doc) => {
      try {
        const updatedDoc = await fetchDocumentPdf(doc, { maxPendingAttempts: 1 });

        if (updatedDoc?.pdfContent) {
          // Update our local array
          const docIndex = updatedDocs.findIndex(d => d.id === doc.id);
          if (docIndex >= 0) {
            updatedDocs[docIndex] = updatedDoc;
          }
        }
      } catch (error) {
        logger.error(`Error prefetching PDF for document ${doc.id}:`, error);
      } finally {
        completedCount++;
        if (onProgress) {
          onProgress(Math.round((completedCount / docsWithPdfLinks.length) * 100));
        }
      }
    }));
  }

  return updatedDocs;
}

/**
 * Checks if there's a pending document for a specific client and template
 *
 * @param clientId The client ID
 * @param templateId The template ID to check
 * @returns Promise with the pending document if found, null otherwise
 */
export async function findPendingDocumentByTemplate(
  clientId: number,
  templateId: number
): Promise<Document | null> {
  try {
    // Look for documents with pending or in_progress status for this client and template
    const pendingDocs = await db.documents
      .where('client_id')
      .equals(clientId)
      .filter(doc =>
        doc.template_id === templateId &&
        (doc.status === 'pending' || doc.status === 'in_progress')
      )
      .toArray();

    // Return the most recently updated pending document
    if (pendingDocs.length > 0) {
      return pendingDocs.sort((a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      )[0];
    }

    return null;
  } catch (error) {
    logger.error('Error finding pending document:', error);
    return null;
  }
}

/** How many clients' document lists are fetched in parallel by refreshDocuments(). */
const REFRESH_DOCUMENTS_CONCURRENCY = 4;

/**
 * Refresh documents for all clients from the server
 * Fetches documents for each client and updates IndexedDB
 */
// API may return { records: [...] }, { documents: [...] }, or just [...]
function parseDocumentsPage(clientId: number, data: unknown): Document[] | null {
  const body = data as Document[] | { records?: Document[]; documents?: Document[] } | null;
  const documents = Array.isArray(body) ? body : body?.records || body?.documents;

  if (!documents || !Array.isArray(documents)) {
    logger.warn(`Unexpected API response format for client ${clientId}:`, data);
    return null;
  }

  return documents;
}

export async function refreshClientDocuments(clientId: number): Promise<void> {
  const currentPerson = await getCurrentPerson();

  if (!currentPerson) {
    logger.debug('No current person - skipping refresh');
    return;
  }

  // Snapshot the moment the server list starts being fetched. The cleanup below
  // must not prune a row written after this instant: it may be a draft a
  // concurrent sync promoted to its server id while we were holding an
  // already-stale list. A skew margin absorbs server-vs-device clock drift
  // (promoted rows carry the server's updated_at).
  const pruneCutoffMs = Date.now() - PRUNE_RECENT_WRITE_MARGIN_MS;

  // Every page is fetched with its own conditional GET (lib/paged-collection):
  // a 304 on page 1 says nothing about page 2, which is where a document started
  // in the main app lands (highest id, last page under id-ASC ordering) — the
  // app used to stop at page 1 and never see it (GHIB-429).
  const collection = await fetchCollection<Document>({
    organizationId: currentPerson.organization.id,
    cacheKey: `documents_${clientId}`,
    url: page => `${API_ENDPOINTS.clients}/${clientId}/documents?current_person_id=${currentPerson.id}&page=${page}`,
    parse: data => parseDocumentsPage(clientId, data),
    // A row filed under another client does not vouch for this client's page.
    localIds: async ids => new Set(await db.documents.where('id').anyOf(ids).filter(d => d.client_id === clientId).primaryKeys()),
    timeout: 30000,
  });

  if (!collection.firstPageOk) {
    // 404: no documents endpoint for this client. 304: a 304 with no manifest to
    // read it against (unreachable with a conforming server; keep local rows,
    // ask again next time). Neither is a failure worth logging.
    if (collection.status !== 404 && collection.status !== 304 && !collection.httpOk) {
      logger.warn(`Failed to fetch documents for client ${clientId}: ${collection.status}`);
    }
    return;
  }

  // Rows from the pages that changed; unchanged pages are already local. An
  // all-304 walk still reconciles below: a row the server no longer lists is
  // pruned whether or not anything else changed.
  const documents: Document[] = collection.records;

  // `allPagesFetched` gates the "remove documents no longer on the server"
  // cleanup below: if any page failed we hold an incomplete server list, so we
  // must not treat the missing documents as server-deleted and prune them.
  const allPagesFetched = collection.complete;
  if (!allPagesFetched) {
    logger.warn(`The documents walk for client ${clientId} was incomplete; keeping local documents until the next refresh`);
  }

  // With no changed rows there can still be a prune to run: a page that answered
  // 200 with an empty body just lost its last document.
  if (documents.length > 0 || allPagesFetched) {
    // Every id the server holds for this client, including the pages that answered 304.
    const serverDocIds = collection.ids;

    // Signature slots ride along in the index payload — persisted into the
    // signatures table (after the documents transaction), never on the documents.
    const signaturesByDoc = new Map<number | string, Signature[]>();
    // Re-opened docs whose ceremony was invalidated server-side: their cached
    // signature rows are stale and must be dropped, not merged.
    const invalidatedCeremonyDocIds: Array<number | string> = [];

    // Rows removed by the cleanup below whose never-synced local values are
    // implicitly discarded (unresolved completed_online conflicts) — tracked
    // after the transaction (trackEvent touches tables this transaction
    // doesn't cover). deleted_online rows with stranded edits are NOT removed
    // (kept for recovery), so they never land here.
    const discardedDocs: Document[] = [];

    // CRDT merge discards collected inside the transaction and emitted after it
    // (trackEvent touches tables outside this transaction's scope).
    const crdtDiscards: Array<{ doc: Pick<Document, 'id' | 'template_id' | 'status'>; discarded: MergeResult['discarded'] }> = [];

    // Resolve the client UUID once, outside the transaction (getClientUUID
    // reads db.applicationSession, which is not in this transaction's scope).
    const clientUUID = await getClientUUID();

    // Template virtual fields, prefetched before the transaction (the
    // documentTemplates table is outside its scope). The editor materializes
    // virtual/disabled computed values into the local form, but every outbound
    // payload strips them — the server can never hold them, so they must not
    // count as local wins in the merges below (they'd mark every doc unsynced
    // and trigger a redundant full-form push per refresh).
    const virtualFieldsByTemplate = new Map<number, Set<string>>();
    for (const templateId of new Set(documents.map(d => d.template_id).filter((id): id is number => id != null))) {
      virtualFieldsByTemplate.set(templateId, await getVirtualFields(templateId));
    }

    // Update documents in IndexedDB, preserving local-only fields
    await db.transaction('rw', db.documents, async () => {
      for (const { signatures: serverSignatures, ...doc } of documents) {
        if (doc.id != null && serverSignatures?.length) signaturesByDoc.set(doc.id, serverSignatures);
        const existingDoc = doc.id != null ? await db.documents.get(doc.id) : undefined;

        // Skip the cleanup while the local copy still has work to replay (an
        // offline re-finalize with captured signatures) — the replay decides
        // the captures' fate, not the pull.
        if (doc.id != null && !serverSignatures?.length && doc.status === 'reopened' &&
            (!existingDoc || !documentNeedsSync(existingDoc))) {
          invalidatedCeremonyDocIds.push(doc.id);
        }

        // ALWAYS CRDT-merge the server copy against the local copy — never gate
        // preservation on the `synced` flag (a stale/wrong flag would otherwise
        // let the server copy overwrite authoritative local edits). HLC stamps
        // decide every field: a local field with no stamp loses to the server, a
        // newer local stamp wins, and a synced doc (local == server) is a no-op.
        let mergedSuperForm = doc.super_form;
        let mergedSuperFormMeta = doc.super_form_meta;
        let mergedClientUuid = doc.client_uuid;
        let mergedSynced = true;
        if (existingDoc && existingDoc.super_form) {
          // Exclude fields this device cannot author server state for — template
          // virtual/disabled fields and restricted sensitive slugs — from the
          // LOCAL side of the merge. Left in, their locally-held values register
          // as spurious local wins on every refresh (the permission-filtered /
          // virtual-stripped server payload never contains them), marking the
          // doc unsynced and triggering redundant pushes; the restricted strip
          // also guarantees a stale sensitive value never survives the merge.
          const local = stripUnauthorableLocal(existingDoc, virtualFieldsByTemplate.get(doc.template_id as number) ?? new Set(), doc.field_permissions);
          const mergeResult = mergeDocumentCRDT(
            { super_form: local.superForm || {}, super_form_meta: local.meta },
            { super_form: doc.super_form || {}, super_form_meta: doc.super_form_meta },
            clientUUID
          );
          mergedSuperForm = mergeResult.merged_form;
          mergedSuperFormMeta = mergeResult.merged_meta;
          mergedClientUuid = existingDoc.client_uuid ?? doc.client_uuid;
          // Stay unsynced if any local value won — those edits still need
          // pushing. Exception: a locked (completed/signing) server doc can't
          // be written back through update, so deriving `synced` from local
          // wins would only start churn — preserve the existing flag instead.
          // A genuinely-unsynced doc keeps pushing via the sweep and surfaces
          // the completed-online conflict; a clean doc stays clean.
          mergedSynced = isDocumentLocked(doc) ? existingDoc.synced !== false : mergeResult.local_wins === 0;
          advanceHLCFromRemoteTimestamps(clientUUID, mergeResult.merged_meta);
          if (mergeResult.discarded.length) {
            crdtDiscards.push({ doc: { id: doc.id, template_id: doc.template_id, status: doc.status }, discarded: mergeResult.discarded });
          }

          // Belt to the local-side strip: also drop restricted slugs from the merged
          // result, mirroring pullDocument, so a stale sensitive value can never
          // survive the merge or leave the device.
          const restrictedAfterMerge = restrictedSlugs(doc.field_permissions);
          if (restrictedAfterMerge.size > 0) {
            const stripped = stripRestrictedFields(mergedSuperForm, mergedSuperFormMeta, restrictedAfterMerge);
            mergedSuperForm = stripped.superForm;
            mergedSuperFormMeta = stripped.meta;
          }
        }
        // Warning dismissals: per key the newer changed_hlc wins, so a pending
        // local dismissal survives an older server copy and is still sent.
        const serverDismissals = serverWarningDismissals(doc.warning_dismissals);
        const mergedDismissals = mergeWarningDismissals(existingDoc?.warning_dismissals, serverDismissals);
        advanceHLCFromRemoteTimestamps(clientUUID, warningDismissalStamps(serverDismissals));
        if (!isDocumentLocked(doc) && mergedDismissals.some((item) => item.pending)) mergedSynced = false;
        // A pending offline finalize owns this row's status until the sync layer
        // replays it (finalize/create_as_finalized). The server copy is
        // pre-finalize, so adopting its status — and dropping locally_finalized,
        // which the server payload doesn't carry — would orphan the replay and
        // leave the server document un-finalized forever.
        const pendingLocalFinalize = Boolean(existingDoc?.locally_finalized && !existingDoc.signatures_synced);
        // A sync error/conflict is a local-only signal the server payload doesn't carry;
        // spreading ...doc would silently clear it and (for permission/other rows) let the
        // retry loop resume, or (for the terminal completed_online conflict) drop the chip
        // and the local edits kept for the resolution modal. Preserve it.
        //
        // The hold (fingerprint + timestamp) travels with the error type it
        // belongs to. Keeping the type but dropping the hold leaves a row that
        // reads as errored yet is no longer held, so the next sweep re-sends the
        // payload the server just refused — and refreshes are frequent enough
        // that this would be most of the retry loop back again. When the merge
        // genuinely changes the local record, the fingerprint stops matching on
        // its own and the document is released for the right reason.
        const preserveErrorState = existingDoc?.sync_error_type != null;
        const mergedDoc: Document = {
          ...doc,
          ...(preserveErrorState ? {
            sync_error_type: existingDoc.sync_error_type,
            sync_error_message: existingDoc.sync_error_message,
            sync_error_fingerprint: existingDoc.sync_error_fingerprint,
            sync_error_at: existingDoc.sync_error_at,
            sync_error_stage: existingDoc.sync_error_stage,
            completed_online_local_super_form: existingDoc.completed_online_local_super_form,
            deleted_online_had_unsynced_edits: existingDoc.deleted_online_had_unsynced_edits,
          } : {}),
          local_id: existingDoc?.local_id, // Preserve local_id for draft URL lookups
          pdfContent: existingDoc?.pdfContent,
          pdfETag: existingDoc?.pdfETag,
          pdfLastModified: existingDoc?.pdfLastModified,
          eTag: existingDoc?.eTag,
          lastModified: existingDoc?.lastModified,
          // CRDT-merged super_form/meta (computed above) — preserves local edits
          // by HLC stamp and folds in any remote changes, independent of `synced`.
          super_form: mergedSuperForm,
          super_form_meta: mergedSuperFormMeta,
          client_uuid: mergedClientUuid,
          warning_dismissals: mergedDismissals,
          synced: !pendingLocalFinalize && mergedSynced,
          ...(pendingLocalFinalize ? {
            locally_finalized: true,
            signatures_synced: existingDoc!.signatures_synced,
            status: existingDoc!.status,
          } : {}),
        };

        await db.documents.put(mergedDoc);
      }

      // Remove synced local documents that no longer exist on the server
      // (e.g. deleted via the web app). Only remove numeric-id records (synced);
      // keep local drafts (string UUID ids) since those haven't reached the server yet.
      const localDocs = await db.documents
        .where('client_id')
        .equals(clientId)
        .toArray();

      for (const local of localDocs) {
        // Remove synced server documents that are no longer in the server response.
        // Skip rows written after the refresh's list fetch began (pruneCutoffMs) —
        // a draft a concurrent auto-sync just promoted to its numeric server id is
        // absent from the stale list but very much alive.
        // Also skip rows that still need sync — a doc absent from the index must not
        // be hard-deleted along with its unsynced edits — and deleted_online rows
        // that stranded never-synced edits when they were marked: those stay (with
        // their "Deleted Online" chip) so the user can recover the work. Other
        // terminal-state rows (plain deleted_online / completed_online) ARE removed
        // so they don't accumulate as ghost rows; when removing an unresolved
        // completed_online conflict drops its kept local edits, that's tracked as
        // a document.discarded.
        const writtenDuringRefresh = local.updated_at !== undefined &&
          new Date(local.updated_at).getTime() >= pruneCutoffMs;
        const strandedDeletedEdits = local.sync_error_type === 'deleted_online' && local.deleted_online_had_unsynced_edits;
        if (allPagesFetched && typeof local.id === 'number' && !serverDocIds.has(local.id) &&
            !writtenDuringRefresh && !strandedDeletedEdits && !documentNeedsSync(local)) {
          logger.debug(`Removing locally cached document ${local.id} (no longer on server)`);
          if (local.sync_error_type === 'completed_online') {
            discardedDocs.push(local);
          }
          await db.documents.delete(local.id);
        }
        // Clean up orphaned drafts whose server counterpart already exists
        if (typeof local.id === 'string' && local.local_id) {
          const hasServerCopy = localDocs.some(
            d => typeof d.id === 'number' && d.local_id === local.local_id
          );
          if (hasServerCopy) {
            logger.debug(`Cleaning up orphaned draft ${local.id}`);
            await db.documents.delete(local.id);
          }
        }
      }
    });

    for (const [docId, serverSignatures] of signaturesByDoc) {
      await storeServerSignatures(docId, serverSignatures);
    }

    for (const docId of invalidatedCeremonyDocIds) {
      await deleteLocalSignatures(docId);
    }

    for (const doc of discardedDocs) {
      trackDocumentDiscarded(doc);
    }

    for (const { doc, discarded } of crdtDiscards) {
      trackCrdtDiscards(doc, discarded);
    }
  }

  // Rows and signatures are stored; the pages that sent them may now answer 304.
  await collection.commit();

  logger.debug(collection.unchanged ? `Documents unchanged for client ${clientId}` : `Refreshed ${documents.length} changed document(s) for client ${clientId}`);
}

export async function refreshDocuments(): Promise<void> {
  const currentPerson = await getCurrentPerson();

  if (!currentPerson) {
    logger.debug('No current person - skipping refresh');
    return;
  }

  // Get all clients for the current organization
  const clients = await db.clients
    .where('organization_id')
    .equals(currentPerson.organization.id)
    .toArray();

  if (clients.length === 0) {
    logger.debug('No clients found - skipping refresh');
    return;
  }

  // One walk per client — a conditional GET per page, usually all 304s, with
  // later pages fetched a few at a time (lib/paged-collection). Run a few
  // clients at a time: serially, a 28-client org spent ~5.5 s of every dashboard
  // load here; the cap keeps a large org from flooding the API on reconnect.
  for (let i = 0; i < clients.length; i += REFRESH_DOCUMENTS_CONCURRENCY) {
    const batch = clients.slice(i, i + REFRESH_DOCUMENTS_CONCURRENCY);
    await Promise.all(batch.map(async (client) => {
      try {
        await refreshClientDocuments(client.id as number);
      } catch (clientError) {
        logger.warn(`Error fetching documents for client ${client.id}:`, clientError);
      }
    }));
  }

  logger.debug(`Refreshed documents across ${clients.length} clients`);
}

/**
 * Pull a single document from the server and update IndexedDB
 * Used when we receive a WebSocket notification that the document has changed
 *
 * @param clientId The client ID (can be UUID string or number)
 * @param documentId The document ID
 * @returns The updated document
 */
export async function pullDocument(clientId: number | string, documentId: number): Promise<Document | null> {
  const currentPerson = await getCurrentPerson();

  if (!currentPerson) {
    logger.debug('No current person - skipping pull');
    return null;
  }

  if (!isOnline()) {
    logger.debug('Offline - skipping pull');
    return null;
  }

  try {
    // Get existing local document before fetch for conditional headers and local-only fields
    const existingDoc = await db.documents.get(documentId);

    const url = `${API_ENDPOINTS.clients}/${clientId}/documents/${documentId}?current_person_id=${currentPerson.id}`;
    const { response, notModified, eTag, lastModified } = await fetchFromApi(url, {
      storedETag: existingDoc?.eTag,
      storedLastModified: existingDoc?.lastModified,
      timeout: PULL_DOCUMENT_TIMEOUT_MS,
    });

    if (notModified) return existingDoc ?? null;

    if (response.status === 404) {
      if (existingDoc) return await markDeletedOnline(existingDoc);
      return null;
    }

    if (response.status === 403) {
      if (existingDoc) {
        const errorData = await response.json().catch(() => ({})) as { message?: string };
        // synced:false, consistent with updateDocument's 403 handling — the row
        // stays retryable via the sweep rather than looking clean.
        return await markPermissionError(existingDoc, errorData.message, false);
      }
      return null;
    }

    if (!response.ok) {
      logger.warn(`Failed to pull document ${documentId}: ${response.status}`);
      return null;
    }

    const serverDoc: Document = await response.json();

    // Merge server data with local data
    let mergedSuperForm = serverDoc.super_form;
    let mergedSuperFormMeta = serverDoc.super_form_meta;
    let mergedSynced = true;
    let shouldAutoSync = false;

    // ALWAYS CRDT-merge the server copy against the local copy — never gate on
    // the `synced` flag. `synced` is a create/update + push-scheduling hint, not
    // a preservation gate; trusting it as one is what lets a stale/wrong flag
    // overwrite authoritative local edits. The merge is safe unconditionally:
    // a local field with no HLC stamp loses to the server, a local field with a
    // newer stamp wins, remote edits win where the server is newer, and a
    // freshly-synced doc (local == server) merges to a no-op.
    if (existingDoc && existingDoc.super_form) {
      const clientUUID = await getClientUUID();

      // Exclude fields this device cannot author server state for — template
      // virtual/disabled fields and restricted sensitive slugs — from the
      // LOCAL side of the merge. Left in, their locally-held values register
      // as spurious local wins on every pull (the server payload never
      // contains them), marking the doc unsynced and scheduling a redundant
      // auto-sync; the restricted strip also guarantees a stale sensitive
      // value never survives the merge or leaves the device.
      const local = await stripUnauthorableLocalForMerge(existingDoc, serverDoc.field_permissions);

      // Perform CRDT merge
      const mergeResult = mergeDocumentCRDT(
        {
          super_form: local.superForm || {},
          super_form_meta: local.meta
        },
        {
          super_form: serverDoc.super_form || {},
          super_form_meta: serverDoc.super_form_meta
        },
        clientUUID
      );

      mergedSuperForm = mergeResult.merged_form;
      mergedSuperFormMeta = mergeResult.merged_meta;

      // Advance local HLC to track causality from remote timestamps
      advanceHLCFromRemoteTimestamps(clientUUID, mergedSuperFormMeta);

      // Persist any local values the merge discarded (server won the conflict)
      // as an offline analytics event — a breadcrumb for recovery/audit.
      trackCrdtDiscards(serverDoc, mergeResult.discarded);

      // Log merge statistics (important for debugging sync issues)
      logger.info('CRDT merge performed during server sync', {
        documentId,
        fieldsDiverged: mergeResult.fields_diverged,
        localWins: mergeResult.local_wins,
        remoteWins: mergeResult.remote_wins,
        hasDivergence: mergeResult.fields_diverged > 0
      });

      // Keep unsynced if there were local wins (need to push back to server).
      // Exception: a locked (completed/signing) server doc can't be written
      // back through update — preserve the existing synced flag instead of
      // deriving it (a genuinely-unsynced doc keeps pushing via the sweep and
      // surfaces the completed-online conflict; a clean doc doesn't start
      // churning), and never schedule an auto-sync against it.
      const serverLocked = isDocumentLocked(serverDoc);
      mergedSynced = serverLocked ? existingDoc.synced !== false : mergeResult.local_wins === 0;

      // Auto-sync merged changes back to server after 1s delay
      if (!serverLocked && mergeResult.local_wins > 0) {
        shouldAutoSync = true;
      }

      // Clean up metadata for fields that no longer exist in merged super_form.
      // Namespaced prefill keys are intentionally preserved — they live in meta
      // without a super_form companion.
      if (mergedSuperForm) {
        const validFields = new Set(Object.keys(mergedSuperForm));
        const cleanedMeta: SuperFormCRDTMetadata = {};
        if (mergedSuperFormMeta) {
          for (const fieldName of Object.keys(mergedSuperFormMeta)) {
            if (validFields.has(fieldName) || fieldName.startsWith('confirmed_prefill:')) {
              cleanedMeta[fieldName] = mergedSuperFormMeta[fieldName];
            }
          }
          mergedSuperFormMeta = cleanedMeta;
        }
      }
    }

    // Strip restricted slugs from the merged result. The server is the source of
    // truth for what the syncing person may view, and it already omits restricted
    // values from its payload — this guards the merge path, where a stale local
    // value could otherwise survive and be auto-synced back to the server.
    const restrictedAfterMerge = restrictedSlugs(serverDoc.field_permissions);
    if (restrictedAfterMerge.size > 0) {
      const stripped = stripRestrictedFields(mergedSuperForm, mergedSuperFormMeta, restrictedAfterMerge);
      mergedSuperForm = stripped.superForm;
      mergedSuperFormMeta = stripped.meta;
    }

    // Warning dismissals: per key the newer changed_hlc wins (see refreshClientDocuments).
    const serverDismissals = serverWarningDismissals(serverDoc.warning_dismissals);
    const mergedDismissals = mergeWarningDismissals(existingDoc?.warning_dismissals, serverDismissals);
    if (serverDismissals?.length) {
      advanceHLCFromRemoteTimestamps(await getClientUUID(), warningDismissalStamps(serverDismissals));
    }
    if (!isDocumentLocked(serverDoc) && mergedDismissals.some((item) => item.pending)) {
      mergedSynced = false;
      shouldAutoSync = true;
    }

    // Strip the legacy field_notes array from server response (we use super_form keys now)
    // Strip the legacy field_notes array and the transient signatures payload —
    // signatures are persisted into their own table below.
    const { field_notes: _unusedFieldNotes, signatures: serverSignatures, ...serverDocWithoutNotes } = serverDoc as Document & { field_notes?: unknown };
    // Same guard as refreshClientDocuments: a pending offline finalize owns
    // status until the sync layer replays it via finalize/create_as_finalized —
    // adopting the server's pre-finalize status would drop locally_finalized
    // and orphan the replay.
    const pendingLocalFinalize = Boolean(existingDoc?.locally_finalized && !existingDoc.signatures_synced);
    const mergedDoc: Document = {
      ...serverDocWithoutNotes,
      local_id: existingDoc?.local_id, // Preserve local_id for draft URL lookups
      pdfContent: existingDoc?.pdfContent,
      pdfETag: existingDoc?.pdfETag,
      pdfLastModified: existingDoc?.pdfLastModified,
      eTag: eTag ?? undefined,
      lastModified: lastModified ?? undefined,
      super_form: mergedSuperForm,
      super_form_meta: mergedSuperFormMeta,
      warning_dismissals: mergedDismissals,
      synced: !pendingLocalFinalize && mergedSynced,
      ...(pendingLocalFinalize ? {
        locally_finalized: true,
        signatures_synced: existingDoc!.signatures_synced,
        status: existingDoc!.status,
      } : {}),
      // A sync error is local-only state the server payload doesn't carry —
      // same preservation as refreshClientDocuments. DocumentEdit pulls on
      // load, so without this the new "Open document" button wiped the very
      // row the user clicked from and released the hold for one more refused
      // PATCH. A merge that genuinely changes the record still releases it,
      // via the fingerprint no longer matching.
      ...(existingDoc?.sync_error_type != null ? {
        sync_error_type: existingDoc.sync_error_type,
        sync_error_message: existingDoc.sync_error_message,
        sync_error_fingerprint: existingDoc.sync_error_fingerprint,
        sync_error_at: existingDoc.sync_error_at,
        sync_error_stage: existingDoc.sync_error_stage,
        completed_online_local_super_form: existingDoc.completed_online_local_super_form,
        deleted_online_had_unsynced_edits: existingDoc.deleted_online_had_unsynced_edits,
      } : {}),
    };

    await db.documents.put(mergedDoc);
    await storeServerSignatures(documentId, serverSignatures);
    // Same guard as refreshClientDocuments: pending local work keeps its captures.
    if (!serverSignatures?.length && mergedDoc.status === 'reopened' &&
        (!existingDoc || !documentNeedsSync(existingDoc))) {
      await deleteLocalSignatures(documentId);
    }

    // Prefetch PDF for offline support
    prefetchPdf(mergedDoc);

    // Auto-sync if needed (after a brief delay to avoid rapid sync loops)
    if (shouldAutoSync && mergedDoc.id && !pendingAutoSyncs.has(mergedDoc.id)) {
      pendingAutoSyncs.add(mergedDoc.id);

      const timer = setTimeout(async () => {
        const docId = mergedDoc.id;
        if (docId) {
          pendingAutoSyncs.delete(docId);
          autoSyncTimers.delete(docId);

          // Check if document is already syncing before auto-sync
          if (syncingDocuments.has(docId)) {
            logger.debug('Document already syncing, skipping auto-sync', { documentId: docId });
            return;
          }

          // Re-read from DB to capture any user edits made during the delay
          const latestDoc = await db.documents.get(docId);
          if (latestDoc) {
            syncDocument(latestDoc).catch(err => {
              logger.error('Failed to auto-sync after CRDT merge', {
                documentId: docId,
                error: err
              });
            });
          }
        }
      }, CRDT_AUTO_SYNC_DELAY_MS);
      autoSyncTimers.set(mergedDoc.id, timer);
    }

    return mergedDoc;
  } catch (error) {
    logger.error(`Error pulling document ${documentId}:`, error);
    return null;
  }
}
